import { isManualCustomSpell } from '@dcc/rules/rules/spellCast.ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AppServices } from '@dcc/rules/app/services.ts';
import type { SheetMutationGate } from '@dcc/rules/app/sheetMutationGate.ts';
import type { CombatParticipant, RoomCombat, SheetsApiStorage } from '../obr/sheetsApiStorage.ts';
import { SheetsSaveError } from '../obr/sheetsApiStorage.ts';
import type { Character } from '@dcc/rules/domain/character.ts';
import type { StatId } from '@dcc/rules/domain/stats.ts';
import { STAT_IDS, STAT_LABELS } from '@dcc/rules/domain/stats.ts';
import {
  createPetCompanion,
  PET_SIZE_VALUES,
  PET_SPECIAL_ABILITY_LABELS,
  PET_MATURE_BENEFIT_LABELS,
  petMatureBenefitLabel,
  petDamageResistance,
  petEvadeModifier,
  petFloorRank,
  petMaxHealthSlots,
  type PetAttitude,
  type PetCompanion,
  type PetOrigin,
  type PetRole,
  type PetSpecialAbilityId,
  validatePetCompanion,
} from '@dcc/rules/domain/pet.ts';
import { statModForScore } from '@dcc/rules/rules/statModifiers.ts';
import { sizeName } from '@dcc/rules/rules/size.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import type { RollMode } from '@dcc/rules/rules/rollModes.ts';
import type { CheckResult } from '@dcc/rules/rules/checks.ts';
import type { Degree } from '@dcc/rules/rules/degrees.ts';
import type { CheckHistoryEntry, DamageHistoryEntry } from '@dcc/rules/rules/rollHistory.ts';
import { addRoll } from '@dcc/rules/rules/rollHistory.ts';
import { newId } from '@dcc/rules/app/ids.ts';
import {
  petAttackIdsFor,
  petAttackProblem,
  planPetAttack,
  rollPetAttackCheck,
  rollPetAttackDamage,
  type PetAttackId,
} from '@dcc/rules/app/petAttackService.ts';
import { dicePlusRequester } from './dicePlusRequester.ts';
import { obtainAdditiveDice, obtainCheckDice, totalMismatchNote } from './diceRolls.ts';
import { damageAllowanceFor, damageOutcomeFor, type GmCall } from './attackPlay.ts';
import { RollBreakdown } from './RollBreakdown.tsx';
import { RollModeContextMenu, type RollModeMenuState } from './RollModeContextMenu.tsx';
import { activeEffects } from '@dcc/rules/rules/encounter.ts';
import { performRoll } from '@dcc/rules/app/playSessionService.ts';
import {
  petCombatEntryProblem,
  petHandlingDifficulty,
  rollPetEvade,
} from '@dcc/rules/app/petCombatService.ts';
import { broadcastCombatSync } from '../obr/combatSync.ts';
import { usePlaySheet } from './usePlaySheet.ts';
import { healPetHealthSlots, petHealthBar, takePetPostMitigationDamage } from '@dcc/rules/app/petHealthService.ts';
import { petHasHeightenedSenses, petMovementOptions, takePetIncomingDamage } from '@dcc/rules/app/petAbilityService.ts';
import {
  castPetSupportSpell,
  payPetSpellCost,
  petSpellCooldownState,
  petSpellProblem,
  petAssignedSpellRank,
  planPetSpellAttack,
  rollPetSpellAttackCheck,
  rollPetSpellAttackDamage,
  startPetSpellCooldown,
} from '@dcc/rules/app/petSpellService.ts';
import {
  petMatureAbilityProblem,
  rollPetEarthquake,
  rollPetWreckingBallDamage,
  applyPetEarthquakeCooldownAfterRoll,
  startPetWreckingBallCooldown,
} from '@dcc/rules/app/petMatureAbilityService.ts';
import { castPetTargetSupportSpell, type PetTargetSpellExecution } from '@dcc/rules/app/petTargetSpellService.ts';
import { petSkillIdsFor, petSkillProblem, rollPetSkillCheck, rollPetStatCheck } from '@dcc/rules/app/petCheckService.ts';
import {
  commitPlayerPetAdvancement,
  previewPlayerPetAdvancement,
  type PetLevelUpAllocation,
  type PetMatureBenefitChoice,
} from '@dcc/rules/app/petProgressionService.ts';

import { PetEquipmentPanel } from './PetEquipmentPanel.tsx';
import { buildPetEquipment, setPetEquipment, petEquipmentMaxMana, type PetEquipment } from '@dcc/rules/app/petEquipmentService.ts';

const equipmentFor = (owner: Character, pet: PetCompanion) => buildPetEquipment(owner, pet, (id) => contentRegistry.getItem(id));

const ATTITUDES: { id: PetAttitude; label: string }[] = [
  { id: 'hostile', label: 'Hostile' },
  { id: 'calm', label: 'Calm' },
  { id: 'friendly', label: 'Friendly' },
  { id: 'bonded', label: 'Bonded' },
];
const ROLES: { id: PetRole; label: string }[] = [
  { id: 'tank', label: 'Tank' },
  { id: 'aggressive', label: 'Aggressive' },
  { id: 'utility', label: 'Utility' },
];
const ABILITIES = Object.entries(PET_SPECIAL_ABILITY_LABELS) as [PetSpecialAbilityId, string][];
const DAMAGE_TYPES = ['Acid', 'Bludgeoning', 'Electric', 'Fire', 'Force', 'Holy', 'Ice', 'Necrotic', 'Piercing', 'Poison', 'Psychic', 'Slashing', 'Sonic'] as const;

function petId(): string {
  return `pet-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function sign(value: number): string { return value >= 0 ? `+${value}` : String(value); }


type CompanionRollTarget =
  | { kind: 'attack'; attackId: PetAttackId }
  | { kind: 'handling' }
  | { kind: 'evade' }
  | { kind: 'stat'; stat: StatId }
  | { kind: 'skill'; skillId: string; stat: StatId }
  | { kind: 'spell'; spellId: string };

function findPetParticipant(combat: RoomCombat | null, characterId: string, petId: string): CombatParticipant | null {
  return combat?.participants.find((participant) =>
    participant.kind === 'pet' && participant.mine && participant.characterId === characterId && participant.sourceId === petId
  ) ?? null;
}

function findCrawlerParticipant(combat: RoomCombat | null, characterId: string): CombatParticipant | null {
  return combat?.participants.find((participant) =>
    participant.kind === 'crawler' && participant.mine && participant.characterId === characterId
  ) ?? null;
}

function withUpdatedCompanion(character: Character, companion: PetCompanion): Character {
  return {
    ...character,
    companions: character.companions.map((pet) => pet.id === companion.id ? companion : pet),
    updatedAt: companion.updatedAt,
  };
}

export function PlayCompanionTab(props: {
  services: AppServices;
  characterId: string;
  refreshToken?: number;
  onSaveError: (error: unknown) => Promise<string>;
  coordinationGate: SheetMutationGate;
  combatStorage: SheetsApiStorage;
}) {
  const [defaultRollMode, setDefaultRollMode] = useState<RollMode>('normal');
  const play = usePlaySheet(props.services, props.characterId, props.onSaveError, props.coordinationGate, props.refreshToken);
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [levelingId, setLevelingId] = useState<string | null>(null);
  const [rollingAttack, setRollingAttack] = useState<PetAttackId | null>(null);
  const [lastAttack, setLastAttack] = useState<{ attackId: PetAttackId; check: CheckResult; entry: CheckHistoryEntry } | null>(null);
  const [lastDamage, setLastDamage] = useState<DamageHistoryEntry | null>(null);
  const [rollMenu, setRollMenu] = useState<(RollModeMenuState & { target: CompanionRollTarget }) | null>(null);
  const [combat, setCombat] = useState<RoomCombat | null>(null);
  const [combatMessage, setCombatMessage] = useState('');
  const [lastHandling, setLastHandling] = useState<CheckHistoryEntry | null>(null);
  const [lastPetEvade, setLastPetEvade] = useState<CheckHistoryEntry | null>(null);
  const [lastCompanionCheck, setLastCompanionCheck] = useState<CheckHistoryEntry | null>(null);
  const [rollingSpellId, setRollingSpellId] = useState<string | null>(null);
  const [lastSpellAttack, setLastSpellAttack] = useState<{ spellId: string; check: CheckResult; entry: CheckHistoryEntry } | null>(null);
  const [lastSpellDamage, setLastSpellDamage] = useState<DamageHistoryEntry | null>(null);
  const [matureResult, setMatureResult] = useState('');
  const [rollingMatureBenefit, setRollingMatureBenefit] = useState<'earthquake' | 'wrecking-ball' | null>(null);

  const refreshCombat = useCallback(async () => {
    try {
      setCombat(await props.combatStorage.getCombat());
    } catch (error) {
      setCombatMessage(error instanceof Error ? error.message : 'Could not refresh companion combat state.');
    }
  }, [props.combatStorage]);

  useEffect(() => {
    void refreshCombat();
    const timer = window.setInterval(() => { void refreshCombat(); }, 2_000);
    return () => window.clearInterval(timer);
  }, [refreshCombat]);

  if (play.error && !play.records) return <p className="dcc-save-status dcc-save-warn" role="alert">{play.error}</p>;
  if (!play.records) return <p>Loading…</p>;

  const { character } = play.records;
  const companion = character.companions[0] ?? null;
  const combatCrawler = findCrawlerParticipant(combat, character.id);
  const combatPet = companion ? findPetParticipant(combat, character.id, companion.id) : null;

  async function saveCompanion(next: PetCompanion): Promise<boolean> {
    const problems = validatePetCompanion(next);
    if (problems.length > 0) return false;
    return play.run('The companion could not be saved.', async (records) => {
      const now = new Date().toISOString();
      const updated = { ...next, updatedAt: now };
      const exists = records.character.companions.some((pet) => pet.id === updated.id);
      const companions = exists
        ? records.character.companions.map((pet) => pet.id === updated.id ? updated : pet)
        : [...records.character.companions, updated];
      return {
        character: { ...records.character, companions, updatedAt: now },
        note: `${updated.name} saved.`,
      };
    });
  }


  async function confirmCompanionAdvancement(
    petId: string,
    allocation: PetLevelUpAllocation,
    matureChoice: PetMatureBenefitChoice | null,
  ): Promise<boolean> {
    const saved = await play.run('The pet level-up could not be saved.', async (records) => {
      const livePet = records.character.companions.find((pet) => pet.id === petId);
      if (!livePet) return 'That companion is no longer attached to this crawler.';
      const now = new Date().toISOString();
      const outcome = commitPlayerPetAdvancement(livePet, records.character.level, allocation, matureChoice, now);
      if (!outcome.ok) return outcome.reason;
      const problems = validatePetCompanion(outcome.pet);
      if (problems.length > 0) return problems.join('; ');
      const levelText = (outcome.levelsGained ?? 0) > 0
        ? `${livePet.name} advanced from Level ${livePet.level} to Level ${outcome.pet.level}. `
        : '';
      return {
        character: withUpdatedCompanion(records.character, outcome.pet),
        note: `${levelText}${livePet.name}'s Stat allocation is committed. Further Stat edits require the GM.`,
      };
    });
    if (saved) setLevelingId(null);
    return saved;
  }

  function removeCompanion(pet: PetCompanion) {
    if (!window.confirm(`Remove ${pet.name} from this crawler? This deletes the saved companion sheet and cannot be undone from the sheet.`)) return;
    void play.run('The companion could not be removed.', async (records) => {
      const now = new Date().toISOString();
      return {
        character: { ...records.character, companions: records.character.companions.filter((entry) => entry.id !== pet.id), updatedAt: now },
        note: `${pet.name} removed.`,
      };
    });
  }

  function applyCompanionDamage(pet: PetCompanion, amount: number, damageType: string) {
    void play.run('The companion damage could not be saved.', async (records) => {
      const livePet = records.character.companions.find((entry) => entry.id === pet.id);
      if (!livePet) return 'That companion is no longer attached to this crawler.';
      const now = new Date().toISOString();
      if (damageType === '__post__') {
        const out = takePetPostMitigationDamage(livePet, amount, now, equipmentFor(records.character, livePet));
        const max = petMaxHealthSlots(out.pet);
        const note = out.healthBar.slotsLost === 0 && amount > 0
          ? `No slot lost — ${out.healthBar.damageIgnored} post-mitigation damage was under ${livePet.name}'s ${out.healthBar.bar.slotValue}-damage Health Bar threshold.`
          : `${livePet.name} lost ${out.healthBar.slotsLost} Health Bar slot(s)${out.healthBar.reducedToZero ? ' and died at 0% Health.' : ` (${out.pet.healthBarSlotsRemaining}/${max} remaining).`}`;
        return { character: withUpdatedCompanion(records.character, out.pet), note };
      }
      const out = takePetIncomingDamage(livePet, records.character.floor, amount, damageType, now, equipmentFor(records.character, livePet));
      const max = petMaxHealthSlots(out.pet);
      const mitigation = out.steps.map((step) => `${step.label}: ${step.running}`).join('; ') + '.';
      const note = out.slotsLost === 0 && out.afterResistance > 0
        ? `${mitigation} No Health Bar slot lost.`
        : `${mitigation} ${livePet.name} lost ${out.slotsLost} Health Bar slot(s)${out.reducedToZero ? ' and died at 0% Health.' : ` (${out.pet.healthBarSlotsRemaining}/${max} remaining).`}`;
      return { character: withUpdatedCompanion(records.character, out.pet), note };
    });
  }

  function healCompanion(pet: PetCompanion, slots: number) {
    void play.run('The companion healing could not be saved.', async (records) => {
      const livePet = records.character.companions.find((entry) => entry.id === pet.id);
      if (!livePet) return 'That companion is no longer attached to this crawler.';
      const now = new Date().toISOString();
      const before = livePet.healthBarSlotsRemaining;
      const updated = healPetHealthSlots(livePet, slots, now);
      return {
        character: withUpdatedCompanion(records.character, updated),
        note: `${livePet.name} healed ${updated.healthBarSlotsRemaining - before} Health Bar slot(s).`,
      };
    });
  }

  function castHealCritter(pet: PetCompanion, execution: PetTargetSpellExecution, spellId = 'heal-critter') {
    const skill = contentRegistry.getSkill(spellId);
    if (!skill) { setCombatMessage('Heal Critter is missing from the loaded rules content.'); return; }
    void play.run('Heal Critter could not be cast.', async (records) => {
      const outcome = castPetTargetSupportSpell(
        records.character, records.runtime, pet.id, skill, { catalogue: { lookupItem: (id) => contentRegistry.getItem(id), lookupSkill: (id) => contentRegistry.getSkill(id) } }, execution, new Date().toISOString(),
      );
      if (!outcome.ok) return outcome.reason;
      return { character: outcome.character, runtime: outcome.runtime, note: outcome.lines.join(' ') };
    });
  }

  async function rollCompanionStat(stat: StatId, mode: RollMode = defaultRollMode) {
    if (!companion) return;
    let entry: CheckHistoryEntry | null = null;
    const saved = await play.run('The companion Stat Check could not be saved.', async (records) => {
      const livePet = records.character.companions.find((pet) => pet.id === companion.id);
      if (!livePet) return 'That companion is no longer attached to this crawler.';
      if (livePet.healthBarSlotsRemaining <= 0) return 'A dead companion cannot make a Stat Check.';
      const resolve = (roller: Parameters<typeof rollPetStatCheck>[0]) => rollPetStatCheck(roller, livePet, stat, mode, equipmentFor(records.character, livePet));
      const label = `${livePet.name}: ${STAT_LABELS[stat].full} Check`;
      const dice = await obtainCheckDice(resolve, label, await dicePlusRequester());
      if (!dice.ok) return dice.reason;
      const check = resolve(dice.roller);
      const createdEntry: CheckHistoryEntry = {
        id: newId(), timestamp: new Date().toISOString(), rollType: 'stat', label,
        total: check.total, statId: stat, statAbbr: STAT_LABELS[stat].abbr,
        statMod: statModForScore(equipmentFor(records.character, livePet).enhancedStats[stat]), skillId: null, skillName: null, skillRank: null,
        mode: check.die.mode, manualMode: mode, effectSources: [], dice: [...check.die.dice], keptDie: check.die.kept,
        extraComponents: check.components.filter((component) => !component.label.endsWith('Mod')).map((component) => ({ ...component })),
        untrained: false, difficulty: null, degree: null,
      };
      entry = createdEntry;
      return {
        session: { ...records.session, rollHistory: addRoll(records.session.rollHistory, createdEntry) },
        note: totalMismatchNote(dice.diceTotal, check.total) ?? dice.note,
      };
    });
    if (saved && entry) setLastCompanionCheck(entry);
  }

  async function rollCompanionSkill(skillId: string, stat: StatId, mode: RollMode = defaultRollMode) {
    if (!companion) return;
    const skill = contentRegistry.getSkill(skillId);
    if (!skill) { setCombatMessage(`Pet Skill ${skillId} is not available in the loaded rules content.`); return; }
    const initialProblem = petSkillProblem(skill);
    if (initialProblem) { setCombatMessage(initialProblem); return; }

    let entry: CheckHistoryEntry | null = null;
    const saved = await play.run('The companion Skill Check could not be saved.', async (records) => {
      const livePet = records.character.companions.find((pet) => pet.id === companion.id);
      if (!livePet) return 'That companion is no longer attached to this crawler.';
      if (livePet.healthBarSlotsRemaining <= 0) return 'A dead companion cannot make a Skill Check.';
      if (!petSkillIdsFor(livePet).includes(skillId)) return `${livePet.name} no longer has ${skill.name}.`;
      const liveProblem = petSkillProblem(skill);
      if (liveProblem) return liveProblem;
      const resolve = (roller: Parameters<typeof rollPetSkillCheck>[0]) => rollPetSkillCheck(roller, livePet, skill, stat, records.character.floor, mode, equipmentFor(records.character, livePet));
      const label = `${livePet.name}: ${skill.name}`;
      const dice = await obtainCheckDice(resolve, label, await dicePlusRequester());
      if (!dice.ok) return dice.reason;
      const check = resolve(dice.roller);
      const createdEntry: CheckHistoryEntry = {
        id: newId(), timestamp: new Date().toISOString(), rollType: 'skill', label,
        total: check.total, statId: stat, statAbbr: STAT_LABELS[stat].abbr,
        statMod: statModForScore(equipmentFor(records.character, livePet).enhancedStats[stat]), skillId: `pet:${livePet.id}:${skill.id}`, skillName: skill.name,
        skillRank: check.components.find((component) => component.label.endsWith('Rank'))?.value ?? records.character.floor, mode: check.die.mode, manualMode: mode, effectSources: [],
        dice: [...check.die.dice], keptDie: check.die.kept,
        extraComponents: check.components
          .filter((component) => !component.label.endsWith('Rank') && !component.label.endsWith('Mod'))
          .map((component) => ({ ...component })),
        untrained: false, difficulty: null, degree: null,
      };
      entry = createdEntry;
      return {
        session: { ...records.session, rollHistory: addRoll(records.session.rollHistory, createdEntry) },
        note: totalMismatchNote(dice.diceTotal, check.total) ?? dice.note,
      };
    });
    if (saved && entry) setLastCompanionCheck(entry);
  }

  async function rollAnimalHandling(mode: RollMode = defaultRollMode) {
    if (!companion) return;
    const eligibility = petCombatEntryProblem(companion);
    if (eligibility) { setCombatMessage(eligibility); return; }

    let fresh: RoomCombat | null;
    try { fresh = await props.combatStorage.getCombat(); }
    catch (error) { setCombatMessage(error instanceof Error ? error.message : 'Could not read combat.'); return; }
    setCombat(fresh);
    const crawlerParticipant = findCrawlerParticipant(fresh, character.id);
    const petParticipant = findPetParticipant(fresh, character.id, companion.id);
    if (!fresh || !crawlerParticipant || !petParticipant) {
      setCombatMessage('This companion was not present when the current combat began.');
      return;
    }
    if (petParticipant.petState === 'active') { setCombatMessage(`${companion.name} is already fighting.`); return; }
    if (petParticipant.petState === 'hidden') { setCombatMessage(`${companion.name} is hiding for the rest of this combat.`); return; }
    if (crawlerParticipant.action1Used && crawlerParticipant.action2Used) {
      setCombatMessage('Your crawler has no Action left to guide their companion this round.');
      return;
    }

    const skill = contentRegistry.getSkill('animal-handling');
    if (!skill) { setCombatMessage('Animal Handling is missing from the loaded rules content.'); return; }
    let degree: Degree | null = null;
    let entry: CheckHistoryEntry | null = null;
    const saved = await play.run('The Animal Handling Check could not be saved.', async (records) => {
      const livePet = records.character.companions.find((pet) => pet.id === companion.id);
      if (!livePet) return 'That companion is no longer attached to this crawler.';
      const liveProblem = petCombatEntryProblem(livePet);
      if (liveProblem) return liveProblem;
      const difficulty = petHandlingDifficulty(livePet, records.character.floor, equipmentFor(records.character, livePet));
      const effects = activeEffects(records.runtime);
      const input = (roller: Parameters<typeof performRoll>[0]) => performRoll(roller, {
        character: records.character,
        session: records.session,
        selection: { stat: 'cha', skill },
        mode,
        difficulty,
        registry: contentRegistry,
        now: new Date().toISOString(),
        newId,
        effects,
        runtime: records.runtime,
      });
      const dice = await obtainCheckDice((roller) => {
        const out = input(roller);
        return out.ok ? out.result : null;
      }, `${livePet.name}: Animal Handling`, await dicePlusRequester());
      if (!dice.ok) return dice.reason;
      const outcome = input(dice.roller);
      if (!outcome.ok) return outcome.reason;
      if (!outcome.resolution) return 'The Animal Handling Check did not resolve against its Difficulty.';
      degree = outcome.resolution.degree;
      const newest = outcome.session.rollHistory[0];
      entry = newest && newest.rollType !== 'damage' ? newest as CheckHistoryEntry : null;
      return {
        character: outcome.character,
        session: outcome.session,
        runtime: outcome.runtime ?? undefined,
        note: totalMismatchNote(dice.diceTotal, outcome.result.total) ?? dice.note,
      };
    });
    if (!saved || !degree) return;
    if (entry) setLastHandling(entry);

    try {
      // Read fresh tracker versions after the several-second Dice+ round trip.
      const current = await props.combatStorage.getCombat();
      const currentCrawler = findCrawlerParticipant(current, character.id);
      const currentPet = findPetParticipant(current, character.id, companion.id);
      if (!current || !currentCrawler || !currentPet) throw new Error('The combat changed while Animal Handling was being rolled.');
      const result = await props.combatStorage.recordPetCombatEntry(
        current.combatId, character.id, companion.id, degree,
        currentCrawler.version, currentPet.version,
      );
      setCombat(result.combat);
      await broadcastCombatSync('pet-combat-entry');
      const messages: Record<typeof result.outcome, string> = {
        joined: `${companion.name} joins the fight. One Attack per round.`,
        frenzied: `${companion.name} joins the fight with full use of both Actions, including up to two Attacks per round.`,
        retry: `${companion.name} stays out of the fight. You may try Animal Handling again with a future Action.`,
        hidden: `${companion.name} runs off and hides for the rest of this combat.`,
      };
      setCombatMessage(`${messages[result.outcome]}${result.outOfActions ? ' The tracker could not charge the crawler Action because both Action slots were already marked.' : ''}`);
    } catch (error) {
      if (error instanceof SheetsSaveError && error.reason === 'combat-stale') await refreshCombat();
      setCombatMessage(error instanceof Error ? error.message : 'The companion combat state could not be updated.');
    }
  }

  async function rollCompanionEvade(mode: RollMode = defaultRollMode) {
    if (!companion) return;
    let fresh: RoomCombat | null;
    try { fresh = await props.combatStorage.getCombat(); }
    catch (error) { setCombatMessage(error instanceof Error ? error.message : 'Could not read combat.'); return; }
    setCombat(fresh);
    const petParticipant = findPetParticipant(fresh, character.id, companion.id);
    if (!fresh || !petParticipant || petParticipant.petState !== 'active') {
      setCombatMessage(`${companion.name} is not currently fighting.`);
      return;
    }
    if (!petParticipant.evadeUsed && petParticipant.action1Used && petParticipant.action2Used) {
      setCombatMessage(`${companion.name} has no Action left to pay for the first Evade this round.`);
      return;
    }

    let entry: CheckHistoryEntry | null = null;
    const saved = await play.run('The companion Evade could not be saved.', async (records) => {
      const livePet = records.character.companions.find((pet) => pet.id === companion.id);
      if (!livePet || livePet.healthBarSlotsRemaining <= 0) return 'A dead companion cannot Evade.';
      const resolve = (roller: Parameters<typeof rollPetEvade>[0]) => rollPetEvade(roller, livePet, mode, equipmentFor(records.character, livePet));
      const dice = await obtainCheckDice(resolve, `${livePet.name}: Evade`, await dicePlusRequester());
      if (!dice.ok) return dice.reason;
      const check = resolve(dice.roller);
      const timestamp = new Date().toISOString();
      const createdEntry: CheckHistoryEntry = {
        id: newId(), timestamp, rollType: 'evade', label: `${livePet.name}: Evade`, total: check.total,
        statId: 'dex', statAbbr: STAT_LABELS.dex.abbr, statMod: statModForScore(equipmentFor(records.character, livePet).enhancedStats.dex),
        skillId: null, skillName: null, skillRank: null, mode: check.die.mode, manualMode: mode,
        effectSources: [], dice: [...check.die.dice], keptDie: check.die.kept,
        extraComponents: check.components.map((component) => ({ ...component })), untrained: false,
        difficulty: null, degree: null,
      };
      entry = createdEntry;
      return {
        session: { ...records.session, rollHistory: addRoll(records.session.rollHistory, createdEntry) },
        note: totalMismatchNote(dice.diceTotal, check.total) ?? dice.note,
      };
    });
    if (!saved || !entry) return;
    setLastPetEvade(entry);
    try {
      const current = await props.combatStorage.getCombat();
      const currentPet = findPetParticipant(current, character.id, companion.id);
      if (!current || !currentPet) throw new Error('The combat changed while Evade was being rolled.');
      const result = await props.combatStorage.recordPetCombatEvade(current.combatId, character.id, companion.id, currentPet.version);
      setCombat(result.combat);
      await broadcastCombatSync('pet-evade');
      setCombatMessage(result.alreadyRecorded
        ? `${companion.name} Evades again. The first Evade already paid this round's Action.`
        : result.outOfActions
          ? `${companion.name} Evaded, but the tracker could not charge an Action because both Action slots were already marked.`
          : `${companion.name}'s first Evade this round spent one Action.`);
    } catch (error) {
      if (error instanceof SheetsSaveError && error.reason === 'combat-stale') await refreshCombat();
      setCombatMessage(error instanceof Error ? error.message : 'The companion Evade could not be added to the tracker.');
    }
  }

  async function rollCompanionAttack(attackId: PetAttackId, rollMode: RollMode = defaultRollMode) {
    if (!companion) return;
    let trackedCombat = false;
    if (combat) {
      let fresh: RoomCombat | null;
      try { fresh = await props.combatStorage.getCombat(); }
      catch (error) { setCombatMessage(error instanceof Error ? error.message : 'Could not read combat.'); return; }
      setCombat(fresh);
      const liveParticipant = findPetParticipant(fresh, character.id, companion.id);
      if (!fresh || !liveParticipant) { setCombatMessage('This companion was not present when the current combat began.'); return; }
      if (liveParticipant.petState !== 'active') { setCombatMessage(`${companion.name} must be guided into the fight before attacking.`); return; }
      if (liveParticipant.petAttackLimit !== null && liveParticipant.petAttacksUsed >= liveParticipant.petAttackLimit) {
        setCombatMessage(`${companion.name} has already used the allowed Attack${liveParticipant.petAttackLimit === 1 ? '' : 's'} this round.`);
        return;
      }
      if (liveParticipant.action1Used && liveParticipant.action2Used) { setCombatMessage(`${companion.name} has no Action left this round.`); return; }
      trackedCombat = true;
    }
    const problem = petAttackProblem(companion);
    if (problem) {
      await play.run('The companion Attack could not be rolled.', async () => problem);
      return;
    }
    const plan = planPetAttack(companion, character.floor, attackId, contentRegistry, equipmentFor(character, companion));
    if (!plan) return;

    const saved = await play.run('The companion Attack could not be saved.', async (records) => {
      const livePet = records.character.companions.find((pet) => pet.id === companion.id);
      if (!livePet) return 'That companion is no longer attached to this crawler.';
      const liveProblem = petAttackProblem(livePet);
      if (liveProblem) return liveProblem;
      const livePlan = planPetAttack(livePet, records.character.floor, attackId, contentRegistry, equipmentFor(records.character, livePet));
      if (!livePlan) return `${livePet.name} does not have that Attack.`;

      const resolve = (roller: Parameters<typeof rollPetAttackCheck>[0]) => rollPetAttackCheck(roller, livePlan, rollMode, null);
      setRollingAttack(attackId);
      try {
        const dice = await obtainCheckDice((roller) => resolve(roller).check, `${livePet.name}: ${livePlan.name}`, await dicePlusRequester());
        if (!dice.ok) return dice.reason;
        const outcome = resolve(dice.roller);
        const timestamp = new Date().toISOString();
        const check = outcome.check;
        const entry: CheckHistoryEntry = {
          id: newId(),
          timestamp,
          rollType: 'attack',
          label: `${livePet.name}: ${livePlan.name}`,
          statId: livePlan.toHitStat,
          statAbbr: STAT_LABELS[livePlan.toHitStat].abbr,
          statMod: livePlan.toHitMod,
          skillId: `pet:${livePet.id}:${livePlan.id}`,
          skillName: livePlan.name,
          skillRank: livePlan.rank,
          mode: check.die.mode,
          manualMode: rollMode,
          effectSources: [],
          dice: [...check.die.dice],
          keptDie: check.die.kept,
          extraComponents: check.components
            .filter((component) => !component.label.endsWith('Rank') && !component.label.endsWith('Mod'))
            .map((component) => ({ ...component })),
          total: check.total,
          untrained: false,
          difficulty: null,
          degree: null,
        };
        const session = { ...records.session, rollHistory: addRoll(records.session.rollHistory, entry) };
        return {
          session,
          after: () => {
            setLastAttack({ attackId, check, entry });
            setLastDamage(null);
          },
          note: totalMismatchNote(dice.diceTotal, check.total) ?? dice.note,
        };
      } finally {
        setRollingAttack(null);
      }
    });
    if (!saved || !trackedCombat) return;
    try {
      const current = await props.combatStorage.getCombat();
      const currentPet = findPetParticipant(current, character.id, companion.id);
      if (!current || !currentPet) throw new Error('The combat changed while the companion Attack was being rolled.');
      const result = await props.combatStorage.spendPetCombatAttack(current.combatId, character.id, companion.id, currentPet.version);
      setCombat(result.combat);
      if (result.attackApplied) await broadcastCombatSync('pet-attack');
      if (result.attackLimitReached) setCombatMessage(`${companion.name}'s Attack was rolled, but another tab already used this round's Attack allowance.`);
      else if (result.outOfActions) setCombatMessage(`${companion.name}'s Attack was rolled, but another tab already used both Actions.`);
      else setCombatMessage(`${companion.name} spent one Action and one Attack allowance.`);
    } catch (error) {
      if (error instanceof SheetsSaveError && error.reason === 'combat-stale') await refreshCombat();
      setCombatMessage(error instanceof Error ? error.message : 'The companion Attack could not be added to the tracker.');
    }
  }

  function rollCompanionDamage(call: GmCall) {
    if (!companion || !lastAttack) return;
    const called = damageOutcomeFor(lastAttack.check, call);
    if (!called?.resolution) return;

    void play.run('The companion damage roll could not be saved.', async (records) => {
      const livePet = records.character.companions.find((pet) => pet.id === companion.id);
      if (!livePet) return 'That companion is no longer attached to this crawler.';
      const plan = planPetAttack(livePet, records.character.floor, lastAttack.attackId, contentRegistry, equipmentFor(records.character, livePet));
      if (!plan) return `${livePet.name} does not have that Attack.`;

      setRollingAttack(lastAttack.attackId);
      try {
        const resolve = (roller: Parameters<typeof rollPetAttackDamage>[0]) =>
          rollPetAttackDamage(roller, plan, called.resolution!.degree, records.character.floor);
        const dice = await obtainAdditiveDice((roller) => resolve(roller).total, `${livePet.name}: ${plan.name} damage`, await dicePlusRequester());
        if (!dice.ok) return dice.reason;
        const damage = resolve(dice.roller);
        const entry: DamageHistoryEntry = {
          id: newId(),
          timestamp: new Date().toISOString(),
          rollType: 'damage',
          label: `${livePet.name}: ${plan.name} damage`,
          total: damage.total,
          effectSources: [],
          skillId: `pet:${livePet.id}:${plan.id}`,
          skillName: plan.name,
          damageType: damage.damageType,
          components: damage.components.map((component) => ({ ...component })),
          criticalHit: damage.criticalHit,
        };
        return {
          session: { ...records.session, rollHistory: addRoll(records.session.rollHistory, entry) },
          after: () => setLastDamage(entry),
          note: totalMismatchNote(dice.diceTotal, damage.total) ?? dice.note,
        };
      } finally {
        setRollingAttack(null);
      }
    });
  }


  async function rollCompanionSpellAttack(spellId: string, rollMode: RollMode = defaultRollMode) {
    if (!companion) return;
    const skill = contentRegistry.getSkill(spellId);
    if (!skill) { setCombatMessage(`Pet Spell ${spellId} is not available in the loaded rules content.`); return; }
    const initialProblem = petSpellProblem(companion, skill, equipmentFor(character, companion));
    if (initialProblem) { setCombatMessage(initialProblem); return; }

    let trackedCombat = false;
    if (combat) {
      let fresh: RoomCombat | null;
      try { fresh = await props.combatStorage.getCombat(); }
      catch (error) { setCombatMessage(error instanceof Error ? error.message : 'Could not read combat.'); return; }
      setCombat(fresh);
      const participant = findPetParticipant(fresh, character.id, companion.id);
      if (!fresh || !participant) { setCombatMessage('This companion was not present when the current combat began.'); return; }
      if (participant.petState !== 'active') { setCombatMessage(`${companion.name} must be guided into the fight before casting an Attack Spell.`); return; }
      if (participant.petAttackLimit !== null && participant.petAttacksUsed >= participant.petAttackLimit) { setCombatMessage(`${companion.name} has used this round's Attack allowance.`); return; }
      if (participant.action1Used && participant.action2Used) { setCombatMessage(`${companion.name} has no Action left this round.`); return; }
      trackedCombat = true;
    }

    let savedCheck: { spellId: string; check: CheckResult; entry: CheckHistoryEntry } | null = null;
    const saved = await play.run('The companion Spell Attack could not be saved.', async (records) => {
      const livePet = records.character.companions.find((pet) => pet.id === companion.id);
      if (!livePet) return 'That companion is no longer attached to this crawler.';
      const liveSkill = contentRegistry.getSkill(spellId);
      if (!liveSkill) return 'That Spell is no longer available in the loaded rules content.';
      const problem = petSpellProblem(livePet, liveSkill, equipmentFor(records.character, livePet));
      if (problem) return problem;
      const plan = planPetSpellAttack(livePet, records.character.floor, liveSkill, contentRegistry, equipmentFor(records.character, livePet));
      if (!plan) return `${liveSkill.name} is not an automated Attack Spell.`;
      const cooldown = petSpellCooldownState(livePet.id, plan, records.runtime);
      if (cooldown.blocked) return cooldown.reason ?? `${liveSkill.name} is on cooldown.`;

      const resolve = (roller: Parameters<typeof rollPetSpellAttackCheck>[0]) => rollPetSpellAttackCheck(roller, plan, rollMode, null);
      setRollingSpellId(spellId);
      try {
        const dice = await obtainCheckDice((roller) => resolve(roller).check, `${livePet.name}: ${liveSkill.name}`, await dicePlusRequester());
        if (!dice.ok) return dice.reason;
        const outcome = resolve(dice.roller);
        const payment = payPetSpellCost(livePet, liveSkill, new Date().toISOString(), equipmentFor(records.character, livePet));
        if (!payment.ok) return payment.reason;
        const timestamp = new Date().toISOString();
        const check = outcome.check;
        const entry: CheckHistoryEntry = {
          id: newId(), timestamp, rollType: 'attack', label: `${livePet.name}: ${liveSkill.name}`,
          total: check.total, statId: plan.toHitStat, statAbbr: STAT_LABELS[plan.toHitStat].abbr, statMod: plan.toHitMod,
          skillId: `pet:${livePet.id}:spell:${liveSkill.id}`, skillName: liveSkill.name, skillRank: plan.rank,
          mode: check.die.mode, manualMode: rollMode, effectSources: [], dice: [...check.die.dice], keptDie: check.die.kept,
          extraComponents: check.components.filter((component) => !component.label.endsWith('Rank') && !component.label.endsWith('Mod')).map((component) => ({ ...component })),
          untrained: false, difficulty: null, degree: null,
        };
        savedCheck = { spellId, check, entry };
        return {
          character: withUpdatedCompanion(records.character, payment.pet),
          runtime: startPetSpellCooldown(livePet.id, plan, records.runtime),
          session: { ...records.session, rollHistory: addRoll(records.session.rollHistory, entry) },
          note: `${liveSkill.name}: -${payment.manaSpent} Mana. ${totalMismatchNote(dice.diceTotal, check.total) ?? dice.note ?? ''}`.trim(),
        };
      } finally { setRollingSpellId(null); }
    });
    if (!saved || !savedCheck) return;
    setLastSpellAttack(savedCheck);
    setLastSpellDamage(null);

    if (!trackedCombat) return;
    try {
      const current = await props.combatStorage.getCombat();
      const participant = findPetParticipant(current, character.id, companion.id);
      if (!current || !participant) throw new Error('The combat changed while the pet Spell was being rolled.');
      const result = await props.combatStorage.spendPetCombatResources(current.combatId, character.id, companion.id, participant.version, { attack: true });
      setCombat(result.combat);
      if (result.attackApplied) await broadcastCombatSync('pet-spell-attack');
      if (result.attackLimitReached) setCombatMessage(`${companion.name}'s Spell was cast, but another tab already used this round's Attack allowance.`);
      else if (result.outOfActions) setCombatMessage(`${companion.name}'s Spell was cast, but another tab already used both Actions.`);
      else setCombatMessage(`${companion.name} spent one Action, one Attack allowance, and the Spell's Mana.`);
    } catch (error) {
      if (error instanceof SheetsSaveError && error.reason === 'combat-stale') await refreshCombat();
      setCombatMessage(error instanceof Error ? error.message : 'The pet Spell could not be added to the combat tracker.');
    }
  }

  function rollCompanionSpellDamage(call: GmCall) {
    if (!companion || !lastSpellAttack) return;
    const called = damageOutcomeFor(lastSpellAttack.check, call);
    if (!called?.resolution) return;
    void play.run('The companion Spell damage could not be saved.', async (records) => {
      const livePet = records.character.companions.find((pet) => pet.id === companion.id);
      const skill = contentRegistry.getSkill(lastSpellAttack.spellId);
      if (!livePet || !skill) return 'That companion Spell is no longer available.';
      const plan = planPetSpellAttack(livePet, records.character.floor, skill, contentRegistry, equipmentFor(records.character, livePet));
      if (!plan) return `${skill.name} is not an automated Attack Spell.`;
      setRollingSpellId(skill.id);
      try {
        const resolve = (roller: Parameters<typeof rollPetSpellAttackDamage>[0]) => rollPetSpellAttackDamage(roller, livePet, records.character.floor, plan, { check: lastSpellAttack.check, resolution: called.resolution }, contentRegistry);
        const dice = await obtainAdditiveDice((roller) => resolve(roller).total, `${livePet.name}: ${skill.name} damage`, await dicePlusRequester());
        if (!dice.ok) return dice.reason;
        const damage = resolve(dice.roller);
        const entry: DamageHistoryEntry = {
          id: newId(), timestamp: new Date().toISOString(), rollType: 'damage', label: `${livePet.name}: ${skill.name} damage`,
          total: damage.total, effectSources: [], skillId: `pet:${livePet.id}:spell:${skill.id}`, skillName: skill.name,
          damageType: damage.damageType, components: damage.components.map((component) => ({ ...component })), criticalHit: damage.criticalHit,
        };
        return { session: { ...records.session, rollHistory: addRoll(records.session.rollHistory, entry) }, after: () => setLastSpellDamage(entry), note: totalMismatchNote(dice.diceTotal, damage.total) ?? dice.note };
      } finally { setRollingSpellId(null); }
    });
  }

  async function castCompanionSupportSpell(spellId: string) {
    if (!companion) return;
    const skill = contentRegistry.getSkill(spellId);
    if (!skill) { setCombatMessage('That pet Spell is missing from the loaded rules content.'); return; }
    let trackedCombat = false;
    if (combat) {
      let fresh: RoomCombat | null;
      try { fresh = await props.combatStorage.getCombat(); }
      catch (error) { setCombatMessage(error instanceof Error ? error.message : 'Could not read combat.'); return; }
      setCombat(fresh);
      const participant = findPetParticipant(fresh, character.id, companion.id);
      if (!fresh || !participant) { setCombatMessage('This companion was not present when the current combat began.'); return; }
      if (participant.petState !== 'active') { setCombatMessage(`${companion.name} must be active before casting in combat.`); return; }
      if (participant.petAttackLimit !== 2) { setCombatMessage('A normal Animal Handling Success limits the pet to Evade, Move, or Attack. Non-Attack Spells require Amazing Success or better.'); return; }
      if (participant.action1Used && participant.action2Used) { setCombatMessage(`${companion.name} has no Action left this round.`); return; }
      trackedCombat = true;
    }
    const saved = await play.run('The companion support Spell could not be cast.', async (records) => {
      const livePet = records.character.companions.find((pet) => pet.id === companion.id);
      const liveSkill = contentRegistry.getSkill(spellId);
      if (!livePet || !liveSkill) return 'That companion Spell is no longer available.';
      const out = castPetSupportSpell(livePet, records.character.floor, liveSkill, new Date().toISOString(), equipmentFor(records.character, livePet));
      if (!out.ok) return out.reason;
      return { character: withUpdatedCompanion(records.character, out.pet), note: out.lines.join(' ') };
    });
    if (!saved || !trackedCombat) return;
    try {
      const current = await props.combatStorage.getCombat();
      const participant = findPetParticipant(current, character.id, companion.id);
      if (!current || !participant) throw new Error('The combat changed while the pet Spell was being cast.');
      const result = await props.combatStorage.spendPetCombatResources(current.combatId, character.id, companion.id, participant.version, {});
      setCombat(result.combat);
      if (result.actionApplied) await broadcastCombatSync('pet-support-spell');
      setCombatMessage(result.outOfActions ? `${companion.name}'s Spell resolved, but another tab already used both Actions.` : `${companion.name} spent one Action and the Spell's Mana.`);
    } catch (error) {
      if (error instanceof SheetsSaveError && error.reason === 'combat-stale') await refreshCombat();
      setCombatMessage(error instanceof Error ? error.message : 'The pet Spell could not be added to the combat tracker.');
    }
  }

  async function markCompanionMovement() {
    if (!companion) return;
    setCombatMessage('');
    setMatureResult('');
    let fresh: RoomCombat | null;
    try { fresh = await props.combatStorage.getCombat(); }
    catch (error) {
      const message = error instanceof Error ? error.message : 'Could not read combat.';
      setCombatMessage(message);
      setMatureResult(message);
      return;
    }
    setCombat(fresh);
    const participant = findPetParticipant(fresh, character.id, companion.id);
    if (!fresh || !participant || participant.petState !== 'active') {
      const message = `${companion.name} must be active in combat before using Movement.`;
      setCombatMessage(message);
      setMatureResult(message);
      return;
    }
    if (participant.movementUsed) {
      const message = `${companion.name}'s Movement is already marked this round.`;
      setCombatMessage(message);
      setMatureResult(message);
      return;
    }
    try {
      const next = await props.combatStorage.setPetCombatTracker(
        fresh.combatId, character.id, companion.id,
        participant.action1Used, participant.action2Used, true, participant.version,
      );
      setCombat(next);
      await broadcastCombatSync('pet-movement');
      const message = `${companion.name} used Movement. Earthquake may now be checked before the melee Attack.`;
      setCombatMessage(message);
      setMatureResult(message);
    } catch (error) {
      if (error instanceof SheetsSaveError && error.reason === 'combat-stale') await refreshCombat();
      const message = error instanceof Error ? error.message : 'The companion Movement could not be marked.';
      setCombatMessage(message);
      setMatureResult(message);
    }
  }

  async function rollEarthquakeBenefit() {
    if (!companion) return;
    setMatureResult('');
    setCombatMessage('');
    let fresh: RoomCombat | null;
    try { fresh = await props.combatStorage.getCombat(); }
    catch (error) {
      const message = error instanceof Error ? error.message : 'Could not read combat.';
      setCombatMessage(message);
      setMatureResult(message);
      return;
    }
    setCombat(fresh);
    const participant = findPetParticipant(fresh, character.id, companion.id);
    if (!fresh || !participant || participant.petState !== 'active') {
      const message = 'Earthquake requires an active pet in combat. Guide the pet into the fight with Animal Handling first.';
      setCombatMessage(message);
      setMatureResult(message);
      return;
    }
    if (!participant.movementUsed) {
      const message = 'Earthquake requires the pet to Move first. Use Mark Move above, then roll the trigger before the melee Attack.';
      setCombatMessage(message);
      setMatureResult(message);
      return;
    }
    if (participant.petAttackLimit !== null && participant.petAttacksUsed >= participant.petAttackLimit) {
      const message = `${companion.name} has no Attack allowance left to follow the Earthquake check.`;
      setCombatMessage(message);
      setMatureResult(message);
      return;
    }

    const preflight = petMatureAbilityProblem(companion, 'earthquake', play.records?.runtime ?? null);
    if (preflight) {
      setMatureResult(preflight);
      return;
    }

    setRollingMatureBenefit('earthquake');
    setMatureResult('Rolling Earthquake trigger…');
    try {
      await play.run('Earthquake could not be rolled.', async (records) => {
        const livePet = records.character.companions.find((pet) => pet.id === companion.id);
        if (!livePet) {
          const message = 'That companion is no longer attached to this crawler.';
          setMatureResult(message);
          return message;
        }
        const problem = petMatureAbilityProblem(livePet, 'earthquake', records.runtime);
        if (problem) {
          setMatureResult(problem);
          return problem;
        }
        const dice = await obtainAdditiveDice((roller) => rollPetEarthquake(roller).die, `${livePet.name}: Earthquake trigger`, await dicePlusRequester());
        if (!dice.ok) {
          setMatureResult(dice.reason);
          return dice.reason;
        }
        const out = rollPetEarthquake(dice.roller);
        return {
          runtime: applyPetEarthquakeCooldownAfterRoll(livePet.id, records.runtime, out.triggers),
          after: () => setMatureResult(out.triggers ? `Earthquake ${out.die}: TRIGGERS. All foes in the ${out.die}ft Burst gain Take Down.` : `Earthquake ${out.die}: no Earthquake.`),
          note: out.triggers ? `Earthquake triggered on ${out.die}.` : `Earthquake did not trigger (${out.die}).`,
        };
      });
    } finally {
      setRollingMatureBenefit(null);
    }
  }

  async function rollWreckingBallBenefit() {
    if (!companion) return;
    let trackedCombat = false;
    if (combat) {
      let fresh: RoomCombat | null;
      try { fresh = await props.combatStorage.getCombat(); }
      catch (error) { setCombatMessage(error instanceof Error ? error.message : 'Could not read combat.'); return; }
      setCombat(fresh);
      const participant = findPetParticipant(fresh, character.id, companion.id);
      if (!fresh || !participant || participant.petState !== 'active') { setCombatMessage(`${companion.name} must be active to use Wrecking Ball in combat.`); return; }
      if (participant.movementUsed) { setCombatMessage(`${companion.name} already used Movement this round; Wrecking Ball requires its Move plus an Attack Action.`); return; }
      if (participant.petAttackLimit !== null && participant.petAttacksUsed >= participant.petAttackLimit) { setCombatMessage(`${companion.name} has no Attack allowance left this round.`); return; }
      if (participant.action1Used && participant.action2Used) { setCombatMessage(`${companion.name} has no Action left this round.`); return; }
      trackedCombat = true;
    }
    let entry: DamageHistoryEntry | null = null;
    const saved = await play.run('Wrecking Ball could not be rolled.', async (records) => {
      const livePet = records.character.companions.find((pet) => pet.id === companion.id);
      if (!livePet) return 'That companion is no longer attached to this crawler.';
      const problem = petMatureAbilityProblem(livePet, 'wrecking-ball', records.runtime);
      if (problem) return problem;
      const resolve = (roller: Parameters<typeof rollPetWreckingBallDamage>[0]) => rollPetWreckingBallDamage(roller, livePet, records.character.floor);
      const dice = await obtainAdditiveDice((roller) => resolve(roller).total, `${livePet.name}: Wrecking Ball damage`, await dicePlusRequester());
      if (!dice.ok) return dice.reason;
      const out = resolve(dice.roller);
      const created: DamageHistoryEntry = {
        id: newId(), timestamp: new Date().toISOString(), rollType: 'damage', label: `${livePet.name}: Wrecking Ball damage`, total: out.total,
        effectSources: [], skillId: `pet:${livePet.id}:wrecking-ball`, skillName: 'Wrecking Ball', damageType: 'Bludgeoning',
        components: [{ label: `${livePet.size}d6 (Size)`, value: out.faces.reduce((a, b) => a + b, 0), faces: out.faces }, { label: `Floor ${records.character.floor}`, value: out.floorBonus }], criticalHit: false,
      };
      entry = created;
      return {
        runtime: startPetWreckingBallCooldown(livePet.id, records.runtime),
        session: { ...records.session, rollHistory: addRoll(records.session.rollHistory, created) },
        after: () => setMatureResult(`Wrecking Ball: ${out.total} Bludgeoning in a 5ft-wide Line out to the pet's Move. The book gives no to-hit Stat for this benefit, so the GM resolves the Area Attack hit.`),
        note: totalMismatchNote(dice.diceTotal, out.total) ?? dice.note,
      };
    });
    if (!saved || !entry || !trackedCombat) return;
    try {
      const current = await props.combatStorage.getCombat();
      const participant = findPetParticipant(current, character.id, companion.id);
      if (!current || !participant) throw new Error('The combat changed while Wrecking Ball was being rolled.');
      const result = await props.combatStorage.spendPetCombatResources(current.combatId, character.id, companion.id, participant.version, { attack: true, movement: true });
      setCombat(result.combat);
      if (result.attackApplied && result.movementApplied) await broadcastCombatSync('pet-wrecking-ball');
      if (result.movementAlreadyUsed) setCombatMessage('Wrecking Ball was rolled, but another tab already used Movement.');
      else if (result.attackLimitReached) setCombatMessage('Wrecking Ball was rolled, but another tab already used the pet Attack allowance.');
      else if (result.outOfActions) setCombatMessage('Wrecking Ball was rolled, but another tab already used both Actions.');
      else setCombatMessage('Wrecking Ball spent one Action, Movement, and one Attack allowance.');
    } catch (error) {
      if (error instanceof SheetsSaveError && error.reason === 'combat-stale') await refreshCombat();
      setCombatMessage(error instanceof Error ? error.message : 'Wrecking Ball could not be added to the combat tracker.');
    }
  }

  const body = !companion ? (
    <section className="dcc-creator-card dcc-companion-empty">
      <h2>Animal Companion</h2>
      <p className="dcc-empty">This crawler does not have a saved animal companion.</p>
      <p className="dcc-creator-help">Pets are linked to their crawler but are not crawlers themselves. Bonded living companions are added to new combats automatically; specially made pet equipment is assigned from the owning crawler’s Inventory.</p>
      <button disabled={play.busy} onClick={() => setEditingId('new')}>Add Companion</button>
    </section>
  ) : (
    <CompanionCard
      character={character}
      pet={companion}
      busy={play.busy || rollingAttack !== null || rollingSpellId !== null}
      rollingAttack={rollingAttack}
      lastAttack={lastAttack}
      lastDamage={lastDamage}
      rollingSpellId={rollingSpellId}
      lastSpellAttack={lastSpellAttack}
      lastSpellDamage={lastSpellDamage}
      matureResult={matureResult}
      rollingMatureBenefit={rollingMatureBenefit}
      onEdit={() => setEditingId(companion.id)}
      onLevelUp={() => setLevelingId(companion.id)}
      onRemove={() => removeCompanion(companion)}
      onApplyDamage={(amount, damageType) => applyCompanionDamage(companion, amount, damageType)}
      onHeal={(slots) => healCompanion(companion, slots)}
      onHealCritter={(execution, spellId) => castHealCritter(companion, execution, spellId)}
      onAttack={(attackId) => void rollCompanionAttack(attackId)}
      onAttackContext={(attackId, event) => {
        event.preventDefault();
        const plan = planPetAttack(companion, character.floor, attackId, contentRegistry, equipmentFor(character, companion));
        if (!plan) return;
        setRollMenu({ x: event.clientX, y: event.clientY, label: `${companion.name}: ${plan.name}`, target: { kind: 'attack', attackId } });
      }}
      onDamage={rollCompanionDamage}
      onSpellAttack={(spellId) => void rollCompanionSpellAttack(spellId)}
      onSpellAttackContext={(spellId, event) => {
        event.preventDefault();
        const spell = contentRegistry.getSkill(spellId);
        if (!spell) return;
        setRollMenu({ x: event.clientX, y: event.clientY, label: `${companion.name}: ${spell.name}`, target: { kind: 'spell', spellId } });
      }}
      onSpellDamage={rollCompanionSpellDamage}
      onSupportSpell={(spellId) => void castCompanionSupportSpell(spellId)}
      onMove={() => void markCompanionMovement()}
      onEarthquake={() => void rollEarthquakeBenefit()}
      onWreckingBall={() => void rollWreckingBallBenefit()}
      combat={combat}
      combatCrawler={combatCrawler}
      combatPet={combatPet}
      combatMessage={combatMessage}
      lastHandling={lastHandling}
      lastPetEvade={lastPetEvade}
      lastCompanionCheck={lastCompanionCheck}
      onRollStat={(stat) => void rollCompanionStat(stat)}
      onRollStatContext={(stat, event) => {
        event.preventDefault();
        setRollMenu({ x: event.clientX, y: event.clientY, label: `${companion.name}: ${STAT_LABELS[stat].full} Check`, target: { kind: 'stat', stat } });
      }}
      onRollSkill={(skillId, stat) => void rollCompanionSkill(skillId, stat)}
      onRollSkillContext={(skillId, stat, event) => {
        event.preventDefault();
        const skill = contentRegistry.getSkill(skillId);
        if (!skill) return;
        setRollMenu({ x: event.clientX, y: event.clientY, label: `${companion.name}: ${skill.name}`, target: { kind: 'skill', skillId, stat } });
      }}
      onGuide={() => void rollAnimalHandling()}
      onGuideContext={(event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setRollMenu({ x: event.clientX, y: event.clientY, label: `${companion.name}: Animal Handling`, target: { kind: 'handling' } });
      }}
      onEvade={() => void rollCompanionEvade()}
      onEvadeContext={(event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setRollMenu({ x: event.clientX, y: event.clientY, label: `${companion.name}: Evade`, target: { kind: 'evade' } });
      }}
    />
  );

  return (
    <div className="dcc-play">
      <div className="dcc-sheet-roll-modes" aria-label="Companion roll mode">{(['normal', 'advantage', 'disadvantage'] as RollMode[]).map(mode => <button type="button" key={mode} disabled={play.busy} className={defaultRollMode === mode ? 'dcc-mini-mode is-selected' : 'dcc-mini-mode'} onClick={() => setDefaultRollMode(mode)}>{mode === 'normal' ? 'Normal' : mode === 'advantage' ? 'Advantage' : 'Disadvantage'}</button>)}</div>
      {body}
      {companion && <PetEquipmentPanel character={character} pet={companion} busy={play.busy} inCombat={combat !== null} onEquip={async (itemId, equipped) => play.run('Pet equipment could not be saved.', async (records) => {
        const freshCombat = await props.combatStorage.getCombat();
        if (freshCombat || records.runtime.encounter) return 'Change pet equipment outside combat.';
        const result = setPetEquipment(records.character, companion.id, itemId, equipped, { lookupItem: (id) => contentRegistry.getItem(id), lookupAttack: (id) => contentRegistry.getAttack(id) }, new Date().toISOString());
        return result.ok ? { character: result.character, note: 'Pet equipment updated.' } : result.reason;
      })} />}
      {editingId && (
        <CompanionEditor
          key={editingId}
          existing={editingId === 'new' ? null : companion}
          ownerLevel={character.level}
          busy={play.busy}
          onCancel={() => setEditingId(null)}
          onSave={async (pet) => {
            const saved = await saveCompanion(pet);
            if (saved) setEditingId(null);
            return saved;
          }}
        />
      )}
      {levelingId && companion?.id === levelingId && (
        <CompanionLevelUpPanel
          key={`${levelingId}-${companion.updatedAt}-${character.level}`}
          pet={companion}
          crawlerLevel={character.level}
          busy={play.busy}
          onCancel={() => setLevelingId(null)}
          onConfirm={(allocation, matureChoice) => confirmCompanionAdvancement(companion.id, allocation, matureChoice)}
        />
      )}
      {play.error && <p className="dcc-save-status dcc-save-warn" role="alert">{play.error}</p>}
      {play.note && <p className="dcc-save-status" role="status">{play.note}</p>}
      <RollModeContextMenu
        menu={rollMenu}
        onClose={() => setRollMenu(null)}
        onChoose={(mode) => {
          const target = rollMenu;
          setRollMenu(null);
          if (!target) return;
          if (target.target.kind === 'attack') void rollCompanionAttack(target.target.attackId, mode);
          else if (target.target.kind === 'handling') void rollAnimalHandling(mode);
          else if (target.target.kind === 'evade') void rollCompanionEvade(mode);
          else if (target.target.kind === 'stat') void rollCompanionStat(target.target.stat, mode);
          else if (target.target.kind === 'skill') void rollCompanionSkill(target.target.skillId, target.target.stat, mode);
          else void rollCompanionSpellAttack(target.target.spellId, mode);
        }}
      />
    </div>
  );
}

function CompanionCard({
  character, pet, busy, rollingAttack, lastAttack, lastDamage, rollingSpellId, lastSpellAttack, lastSpellDamage, matureResult, rollingMatureBenefit,
  onEdit, onLevelUp, onRemove, onApplyDamage, onHeal, onHealCritter, onAttack, onAttackContext, onDamage,
  onSpellAttack, onSpellAttackContext, onSpellDamage, onSupportSpell, onMove, onEarthquake, onWreckingBall,
  combat, combatCrawler, combatPet, combatMessage, lastHandling, lastPetEvade, lastCompanionCheck,
  onRollStat, onRollStatContext, onRollSkill, onRollSkillContext,
  onGuide, onGuideContext, onEvade, onEvadeContext,
}: {
  character: Character;
  pet: PetCompanion;
  busy: boolean;
  rollingAttack: PetAttackId | null;
  lastAttack: { attackId: PetAttackId; check: CheckResult; entry: CheckHistoryEntry } | null;
  lastDamage: DamageHistoryEntry | null;
  rollingSpellId: string | null;
  lastSpellAttack: { spellId: string; check: CheckResult; entry: CheckHistoryEntry } | null;
  lastSpellDamage: DamageHistoryEntry | null;
  matureResult: string;
  rollingMatureBenefit: 'earthquake' | 'wrecking-ball' | null;
  onEdit: () => void;
  onLevelUp: () => void;
  onRemove: () => void;
  onApplyDamage: (amount: number, damageType: string) => void;
  onHeal: (slots: number) => void;
  onHealCritter: (execution: PetTargetSpellExecution, spellId?: string) => void;
  onAttack: (attackId: PetAttackId) => void;
  onAttackContext: (attackId: PetAttackId, event: React.MouseEvent<HTMLButtonElement>) => void;
  onDamage: (call: GmCall) => void;
  onSpellAttack: (spellId: string) => void;
  onSpellAttackContext: (spellId: string, event: React.MouseEvent<HTMLButtonElement>) => void;
  onSpellDamage: (call: GmCall) => void;
  onSupportSpell: (spellId: string) => void;
  onMove: () => void;
  onEarthquake: () => void;
  onWreckingBall: () => void;
  combat: RoomCombat | null;
  combatCrawler: CombatParticipant | null;
  combatPet: CombatParticipant | null;
  combatMessage: string;
  lastHandling: CheckHistoryEntry | null;
  lastPetEvade: CheckHistoryEntry | null;
  lastCompanionCheck: CheckHistoryEntry | null;
  onRollStat: (stat: StatId) => void;
  onRollStatContext: (stat: StatId, event: React.MouseEvent<HTMLButtonElement>) => void;
  onRollSkill: (skillId: string, stat: StatId) => void;
  onRollSkillContext: (skillId: string, stat: StatId, event: React.MouseEvent<HTMLButtonElement>) => void;
  onGuide: () => void;
  onGuideContext: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onEvade: () => void;
  onEvadeContext: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const maxHealth = petMaxHealthSlots(pet);
  const advancementPreview = previewPlayerPetAdvancement(pet, character.level);
  const equipment = equipmentFor(character, pet);
  const healthBar = petHealthBar(pet, equipment);
  const [damageAmount, setDamageAmount] = useState(healthBar.slotValue);
  const [damageType, setDamageType] = useState<string>('Slashing');
  const floorRank = petFloorRank(character.floor);
  const dr = petDamageResistance(pet, character.floor) + equipment.modifiers.damageResistance;
  const maxMana = petEquipmentMaxMana(pet, equipment);
  const healCritter = contentRegistry.getSkill('heal-critter');
  const knowsHealCritter = !!healCritter && character.skills.some((entry) => entry.skillId === healCritter.id && entry.rank >= 1);
  const customPetHeals = character.skills.map(e => contentRegistry.getSkill(e.skillId)).filter((s): s is NonNullable<typeof s> => !!s && s.id !== 'heal-critter' && s.kind === 'spell' && s.supportEffect?.target === 'pet');
  const petSpells = pet.spellIds.map((id) => contentRegistry.getSkill(id)).filter((skill): skill is NonNullable<typeof skill> => !!skill && skill.kind === 'spell');
  const petSkillIds = petSkillIdsFor(pet);
  const petSkills = petSkillIds.map((id) => contentRegistry.getSkill(id)).filter((skill): skill is NonNullable<typeof skill> => !!skill);
  const unknownPetSkillIds = petSkillIds.filter((id) => !contentRegistry.getSkill(id));
  const [skillStats, setSkillStats] = useState<Record<string, StatId>>({});
  const petGear = pet.gearItemIds.map((id) => contentRegistry.getItem(id));
  const movementOptions = petMovementOptions(pet);
  const heightenedSenses = petHasHeightenedSenses(pet);
  const spellAllowance = lastSpellAttack ? damageAllowanceFor(lastSpellAttack.check) : null;
  const stats = useMemo(() => STAT_IDS.map((id) => ({ id, value: equipment.enhancedStats[id], mod: statModForScore(equipment.enhancedStats[id]) })), [pet, character]);
  const attackIds = petAttackIdsFor(pet);
  const attackPlans = attackIds.map((id) => planPetAttack(pet, character.floor, id, contentRegistry, equipment)).filter((plan): plan is NonNullable<typeof plan> => plan !== null);
  const attackProblem = petAttackProblem(pet);
  const allowance = lastAttack ? damageAllowanceFor(lastAttack.check) : null;
  const combatAttackProblem = !combat ? null
    : !combatPet ? 'This companion was not present when this combat began. End and restart combat to snapshot the companion.'
      : combatPet.petState === 'waiting' ? `Use Animal Handling in the Combat section above before ${pet.name} can Attack this round.`
        : combatPet.petState === 'hidden' ? `${pet.name} is hiding for the rest of this combat.`
        : combatPet.petState !== 'active' ? `${pet.name} is not currently fighting.`
        : combatPet.petAttackLimit !== null && combatPet.petAttacksUsed >= combatPet.petAttackLimit ? `${pet.name} has used this round's Attack allowance.`
          : combatPet.action1Used && combatPet.action2Used ? `${pet.name} has no Action left this round.`
            : null;
  return (
    <section className="dcc-creator-card dcc-companion-card">
      <div className="dcc-companion-heading">
        <div>
          <h2>{pet.name}</h2>
          <p className="dcc-creator-help">{pet.species} · Level {pet.level} · {sizeName(pet.size)} ({pet.size}) · {pet.attitude}</p>
        </div>
        <div className="dcc-creator-nav">
          <button className="dcc-creator-secondary" disabled={busy} onClick={onEdit}>Edit Companion</button>
          <button disabled={busy || !advancementPreview.canAdvance} onClick={onLevelUp} title={advancementPreview.canAdvance ? 'Allocate all pending pet advancement and confirm it permanently.' : 'No pet level-up or Stat allocation is pending.'}>Level Up Pet</button>
          <button className="dcc-danger" disabled={busy} onClick={onRemove}>Remove</button>
        </div>
      </div>
      <div className="dcc-companion-vitals">
        <div><span>Floor Rank</span><strong>{floorRank}</strong></div>
        <div><span>Evade</span><strong>{sign(petEvadeModifier({ unenhancedStats: equipment.enhancedStats }) + equipment.modifiers.evade)}</strong></div>
        <div><span>Speed</span><strong>{pet.specialAbilities.includes('move-60') ? '60ft' : '20+S'}</strong></div>
        <div><span>DR</span><strong>{dr}</strong></div>
      </div>
      <div className="dcc-companion-combat">
        <div className="dcc-companion-health-head">
          <strong>Combat</strong>
          <span>{!combat ? 'No active combat' : combatPet?.petState === 'active' ? `Round ${combat.round} · Active` : combatPet?.petState === 'hidden' ? `Round ${combat.round} · Hiding` : `Round ${combat.round} · Waiting`}</span>
        </div>
        {!combat ? (
          <p className="dcc-creator-help">When the GM starts combat, a Bonded living companion is snapshotted with its crawler and waits for Animal Handling.</p>
        ) : !combatPet ? (
          <p className="dcc-creator-warn">This companion was not present when this combat began. It will join the tracker the next time combat starts.</p>
        ) : combatPet.petState === 'waiting' ? (
          <>
            <p className="dcc-creator-help">Spend one crawler Action and make an Int-Opposed Animal Handling Check to bring {pet.name} into the fight.</p>
            <div className="dcc-creator-nav">
              <button disabled={busy || !combatCrawler || (combatCrawler.action1Used && combatCrawler.action2Used)} onClick={onGuide} onContextMenu={onGuideContext} title="Right-click for Straight / Advantage / Disadvantage.">Animal Handling</button>
            </div>
          </>
        ) : combatPet.petState === 'hidden' ? (
          <p className="dcc-creator-warn">{pet.name} ran out of sight after a Major Fail or worse and is hiding for the rest of this combat.</p>
        ) : (
          <>
            <div className="dcc-combat-dots" aria-label={`${pet.name} combat resources`}>
              <span className={combatPet.action1Used ? 'dcc-combat-resource-chip is-used' : 'dcc-combat-resource-chip'}>A1</span>
              <span className={combatPet.action2Used ? 'dcc-combat-resource-chip is-used' : 'dcc-combat-resource-chip'}>A2</span>
              <span className={combatPet.movementUsed ? 'dcc-combat-resource-chip is-used' : 'dcc-combat-resource-chip'}>M</span>
              <span className={combatPet.evadeUsed ? 'dcc-combat-resource-chip is-used' : 'dcc-combat-resource-chip'} title="Evade paid this round">E</span>
              <span className="dcc-combat-pet-attacks">ATK {combatPet.petAttacksUsed}/{combatPet.petAttackLimit ?? 1}</span>
            </div>
            <div className="dcc-creator-nav">
              <button className="dcc-creator-secondary" disabled={busy || combatPet.movementUsed} onClick={onMove}>{combatPet.movementUsed ? 'Movement Used' : 'Mark Move'}</button>
              <button className="dcc-creator-secondary" disabled={busy || (!combatPet.evadeUsed && combatPet.action1Used && combatPet.action2Used)} onClick={onEvade} onContextMenu={onEvadeContext} title="First Evade each round costs an Action. Right-click for roll mode.">Roll Evade</button>
            </div>
            <p className="dcc-creator-help">{combatPet.petAttackLimit === 2 ? 'Amazing Success: both Actions may be used freely, including two Attacks.' : 'Standard Success: two Actions, but no more than one Attack each round.'} First Evade each round costs one Action; later Evades that round are free.</p>
          </>
        )}
        {combatMessage && <p className="dcc-save-status" role="status">{combatMessage}</p>}
        {lastHandling && <RollBreakdown entry={lastHandling} />}
        {lastPetEvade && <RollBreakdown entry={lastPetEvade} />}
      </div>
      <div className="dcc-companion-health">
        <div className="dcc-companion-health-head">
          <strong>Health Bar</strong>
          <span>{pet.healthBarSlotsRemaining}/{maxHealth}{pet.healthBarSlotsRemaining === 0 ? ' · DEAD' : ''}</span>
        </div>
        <CompanionHealthBar pet={pet} equipment={equipment} />
        <p className="dcc-creator-help dcc-companion-health-rule">CON Mod {sign(healthBar.slotValue)}. Each Health Bar slot requires {healthBar.slotValue} post-mitigation damage to mark off. Damage below one full slot is ignored.</p>
        <div className="dcc-companion-damage-controls">
          <label className="dcc-creator-field">Incoming damage
            <input
              type="number"
              min={0}
              step={1}
              value={damageAmount}
              disabled={busy || pet.healthBarSlotsRemaining <= 0}
              onChange={(event) => setDamageAmount(Math.max(0, Math.floor(Number(event.target.value) || 0)))}
            />
          </label>
          <label className="dcc-creator-field">Damage type
            <select value={damageType} disabled={busy} onChange={(event) => setDamageType(event.target.value)}>
              {DAMAGE_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
              <option value="__post__">Already mitigated</option>
            </select>
          </label>
          <button disabled={busy || pet.healthBarSlotsRemaining <= 0 || damageAmount <= 0} onClick={() => onApplyDamage(damageAmount, damageType)}>Apply Damage</button>
          <button className="dcc-creator-secondary" disabled={busy || pet.healthBarSlotsRemaining >= maxHealth} onClick={() => onHeal(1)}>Heal 1 HB</button>
          {customPetHeals.map(spell => <button key={spell.id} className="dcc-creator-secondary" disabled={busy || pet.healthBarSlotsRemaining <= 0 || pet.healthBarSlotsRemaining >= maxHealth || character.currentMana < (spell.manaCost ?? 0)} onClick={() => onHealCritter('normal', spell.id)}>Cast {spell.name} · {spell.manaCost} Mana</button>)}
          {knowsHealCritter && <button className="dcc-creator-secondary" disabled={busy || pet.healthBarSlotsRemaining <= 0 || pet.healthBarSlotsRemaining >= maxHealth || character.currentMana < (healCritter?.manaCost ?? 0)} onClick={() => onHealCritter('normal')}>Cast Heal Critter</button>}
          {knowsHealCritter && combat?.phase === 'crawler-reaction' && <button className="dcc-creator-secondary" disabled={busy || pet.healthBarSlotsRemaining <= 0 || pet.healthBarSlotsRemaining >= maxHealth || character.currentMana < (healCritter?.manaCost ?? 0)} onClick={() => onHealCritter('healing-interrupt')}>Heal Critter Interrupt</button>}
        </div>
        <p className="dcc-creator-help">Raw damage automatically applies Tank DR ({dr}) first, then matching elemental Resistance, then Health Bar thresholds. Choose “Already mitigated” for mixed damage or GM-resolved damage you have already reduced.</p>
      </div>
      <div className="dcc-companion-checks">
        <div className="dcc-companion-health-head">
          <strong>Attributes</strong>
          <span>Click to roll · use the roll-mode buttons above</span>
        </div>
        <div className="dcc-stat-grid">
          {stats.map(({ id, value, mod }) => (
            <button
              key={id}
              type="button"
              className="dcc-stat dcc-companion-stat-roll"
              disabled={busy || pet.healthBarSlotsRemaining <= 0}
              onClick={() => onRollStat(id)}
              onContextMenu={(event) => onRollStatContext(id, event)}
              title={`Roll ${STAT_LABELS[id].full} Check. Right-click for Straight / Advantage / Disadvantage.`}
            >
              <span className="dcc-stat-label">{STAT_LABELS[id].abbr.toUpperCase()}</span>
              <span className="dcc-stat-score">{value}</span>
              <span className="dcc-stat-mod">{sign(mod)}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="dcc-companion-skills">
        <div className="dcc-companion-health-head">
          <strong>Skills</strong>
          <span>Rank {floorRank} = Floor</span>
        </div>
        {petSkills.length === 0 ? <p className="dcc-creator-help">No GM-granted or Special-Ability Skills are assigned to this companion yet.</p> : (
          <div className="dcc-companion-skill-list">
            {petSkills.map((skill) => {
              const problem = petSkillProblem(skill);
              const selectedStat = skillStats[skill.id] ?? skill.commonStat ?? 'str';
              return (
                <div key={skill.id} className="dcc-companion-skill-row">
                  <div>
                    <strong>{skill.name}</strong>
                    <span>{problem ?? `d20 + Rank ${Math.max(0, Math.min(skill.maxRank ?? 20, floorRank + (equipment.modifiers.skillRankBonuses[skill.id] ?? 0)))} + selected Stat Mod`}</span>
                  </div>
                  {problem ? <span className="dcc-creator-help">Passive / separate action</span> : (
                    <>
                      <select
                        aria-label={`${skill.name} Stat`}
                        value={selectedStat}
                        disabled={busy}
                        onChange={(event) => setSkillStats((current) => ({ ...current, [skill.id]: event.target.value as StatId }))}
                      >
                        {STAT_IDS.map((stat) => <option key={stat} value={stat}>{STAT_LABELS[stat].abbr}</option>)}
                      </select>
                      <button
                        disabled={busy || pet.healthBarSlotsRemaining <= 0}
                        onClick={() => onRollSkill(skill.id, selectedStat)}
                        onContextMenu={(event) => onRollSkillContext(skill.id, selectedStat, event)}
                        title="Right-click to roll Straight, with Advantage, or with Disadvantage."
                      >Roll Skill</button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {unknownPetSkillIds.length > 0 && <p className="dcc-creator-warn">Unknown GM-assigned pet Skill ID{unknownPetSkillIds.length === 1 ? '' : 's'}: {unknownPetSkillIds.join(', ')}</p>}
        <p className="dcc-creator-help">Pet Skills do not train or keep permanent Ranks. Rollable pet Skills use the current Floor as Rank. The GM assigns additional Skills in Companion Management.</p>
        {lastCompanionCheck && <RollBreakdown entry={lastCompanionCheck} />}
      </div>
      <div className="dcc-companion-attacks">
        <div className="dcc-companion-health-head">
          <strong>Attacks</strong>
          <span>Rank {floorRank} = Floor</span>
        </div>
        {attackProblem && <p className="dcc-creator-warn">{attackProblem}</p>}
        <div className="dcc-companion-attack-list">
          {attackPlans.map((plan) => (
            <div key={plan.id} className="dcc-companion-attack-row">
              <div>
                <strong>{plan.name}</strong>
                <span>{plan.toHitFormula}</span>
                <span>{plan.damageFormula}</span>
                {plan.notes.map((note) => <span key={note}>{note}</span>)}
              </div>
              <button
                disabled={busy || attackProblem !== null}
                onClick={() => onAttack(plan.id)}
                onContextMenu={(event) => onAttackContext(plan.id, event)}
                title="Right-click to roll straight, with Advantage, or with Disadvantage."
              >{rollingAttack === plan.id ? 'Rolling…' : 'Roll Attack'}</button>
            </div>
          ))}
        </div>
        {combatAttackProblem && combat && <p className="dcc-creator-help">{combatAttackProblem}</p>}
      </div>
      {lastAttack && (
        <div className="dcc-companion-attack-result">
          <RollBreakdown entry={lastAttack.entry} />
          {allowance?.kind === 'critical-fail' && <p className="dcc-creator-warn">Natural 1: Critical Fail. No damage.</p>}
          {allowance?.kind === 'critical-hit' && (
            <>
              <p><strong>Natural 20: Critical Hit.</strong> All Floor-scaled base damage dice are doubled.</p>
              <div className="dcc-creator-nav"><button disabled={busy} onClick={() => onDamage('hit')}>Roll Critical damage</button></div>
            </>
          )}
          {allowance?.kind === 'gm-decides' && (
            <>
              <p className="dcc-creator-help">If the GM says it hit, roll damage. Amazing Success adds +Floor damage in addition to any Aggressive role bonus.</p>
              <div className="dcc-creator-nav">
                <button disabled={busy} onClick={() => onDamage('hit')}>Hit: roll damage</button>
                <button className="dcc-creator-secondary" disabled={busy} onClick={() => onDamage('amazing')}>Amazing Success</button>
              </div>
            </>
          )}
          {lastDamage && <RollBreakdown entry={lastDamage} />}
        </div>
      )}
      <div className="dcc-companion-spells">
        <div className="dcc-companion-health-head">
          <strong>Pet Spells</strong>
          <span>{pet.currentMana}/{maxMana} Mana</span>
        </div>
        {pet.matureBenefit?.id !== 'spells' && petSpells.length > 0 && <p className="dcc-creator-warn">Spells are assigned, but this pet does not have the Level-15 Spells maturity benefit, so it cannot cast them.</p>}
        {petSpells.length === 0 ? <p className="dcc-creator-help">No pet Spells assigned by the GM.</p> : (
          <div className="dcc-companion-attack-list">
            {petSpells.map((spell) => {
              const rank = petAssignedSpellRank(pet, spell, character.floor);
              const problem = petSpellProblem(pet, spell, equipment);
              const attackProfile = contentRegistry.getAttack(spell.id);
              const automatedSupport = !!spell.supportEffect || isManualCustomSpell(spell);
              const attackPlan = attackProfile ? planPetSpellAttack(pet, character.floor, spell, contentRegistry, equipment) : null;
              return (
                <div key={spell.id} className="dcc-companion-attack-row">
                  <div>
                    <strong>{spell.name}</strong>
                    <span>Rank {rank} · {spell.manaCost ?? 0} Mana{attackProfile ? ' · Attack Spell' : spell.traits.includes('passive') ? ' · Passive Spell' : ''}</span>
                    {attackPlan && <><span>{attackPlan.toHitFormula}</span><span>{attackPlan.damageFormula}</span></>}
                    {problem && <span className="dcc-creator-warn">{problem}</span>}
                    {!attackProfile && !automatedSupport && <span className="dcc-creator-help">Recorded, but this Spell's effect is not automated yet.</span>}
                  </div>
                  {attackProfile ? (
                    <button
                      disabled={busy || problem !== null}
                      onClick={() => onSpellAttack(spell.id)}
                      onContextMenu={(event) => onSpellAttackContext(spell.id, event)}
                      title="Right-click for Straight / Advantage / Disadvantage."
                    >{rollingSpellId === spell.id ? 'Casting…' : 'Roll Spell Attack'}</button>
                  ) : automatedSupport ? (
                    <button disabled={busy || problem !== null} onClick={() => onSupportSpell(spell.id)}>{rollingSpellId === spell.id ? 'Casting…' : 'Cast Spell'}</button>
                  ) : <button disabled>Manual effect</button>}
                </div>
              );
            })}
          </div>
        )}
        <p className="dcc-creator-help">Pet Spell Rank defaults to Floor, subject to any printed maximum Rank. A GM-assigned Rank override is shown as GM. Pets have no Class, so Favored Spell surcharges do not apply. Attack Spells use an Attack allowance; non-Attack Spells in combat require the unrestricted two-Action result from an Amazing Animal Handling Success or better.</p>
      </div>
      {lastSpellAttack && (
        <div className="dcc-companion-attack-result">
          <RollBreakdown entry={lastSpellAttack.entry} />
          {spellAllowance?.kind === 'critical-fail' && <p className="dcc-creator-warn">Natural 1: Critical Fail. No damage.</p>}
          {spellAllowance?.kind === 'critical-hit' && <div className="dcc-creator-nav"><button disabled={busy} onClick={() => onSpellDamage('hit')}>Roll Critical damage</button></div>}
          {spellAllowance?.kind === 'gm-decides' && (
            <div className="dcc-creator-nav">
              <button disabled={busy} onClick={() => onSpellDamage('hit')}>Hit: roll damage</button>
              <button className="dcc-creator-secondary" disabled={busy} onClick={() => onSpellDamage('amazing')}>Amazing Success</button>
            </div>
          )}
          {lastSpellDamage && <RollBreakdown entry={lastSpellDamage} />}
        </div>
      )}
      <div className="dcc-companion-abilities">
        <div className="dcc-companion-health-head">
          <strong>Movement & Special Abilities</strong>
          <span>{pet.role === 'tank' ? `Tank DR ${dr}` : 'Pet mechanics'}</span>
        </div>
        <ul>{movementOptions.map((option) => <li key={option.mode}>{option.label}</li>)}</ul>
        {heightenedSenses && <p className="dcc-creator-help"><strong>Heightened Senses:</strong> use the Attribute roll buttons above against the same Difficulty the crawler faced.</p>}
        {pet.specialAbilities.includes('resistance-uncommon') && <p><strong>Resistance:</strong> {pet.resistanceDamageType || 'damage type not selected'}{pet.resistanceDamageType ? ' damage is halved after DR.' : ''}</p>}
        {pet.matureBenefit?.id === 'earthquake' && (
          <>
            <p className="dcc-creator-help"><strong>Earthquake:</strong> the pet must be Active and its Movement must be marked before this trigger is rolled. Roll it before the melee Attack.</p>
            <div className="dcc-creator-nav"><button disabled={busy || pet.healthBarSlotsRemaining <= 0} onClick={onEarthquake}>{rollingMatureBenefit === 'earthquake' ? 'Rolling Earthquake…' : 'Roll Earthquake Trigger'}</button></div>
          </>
        )}
        {pet.matureBenefit?.id === 'wrecking-ball' && (
          <div className="dcc-creator-nav"><button disabled={busy || pet.healthBarSlotsRemaining <= 0} onClick={onWreckingBall}>Use Wrecking Ball</button></div>
        )}
        {pet.matureBenefit?.id === 'regeneration' && <p className="dcc-creator-help"><strong>Regeneration:</strong> death triggers +25% Stats and a new weakness; every fifth death resets the pet to Level 1. The rule does not specify rounding or weakness selection, so the GM resolves those changes rather than the sheet fabricating them.</p>}
        {matureResult && <p className="dcc-save-status" role="status">{matureResult}</p>}
      </div>
      <div className="dcc-companion-details">
        <p><strong>Origin:</strong> {pet.origin === 'earth-born' ? 'Earth-born' : 'Dungeon-born'}{pet.crawlerNumber ? ` · ID ${pet.crawlerNumber}` : ''}</p>
        <p><strong>Role:</strong> {pet.role ? pet.role.charAt(0).toUpperCase() + pet.role.slice(1) : 'None until Bonded'}</p>
        {pet.role === 'aggressive' && <p><strong>Aggressive:</strong> Back Claw and Bite use Rank = Floor and animal attacks add +Floor damage.</p>}
        {pet.specialAbilities.length > 0 && (
          <div><strong>Special Abilities</strong><ul>{pet.specialAbilities.map((ability) => <li key={ability}>{PET_SPECIAL_ABILITY_LABELS[ability]}{ability === 'resistance-uncommon' && pet.resistanceDamageType ? `: ${pet.resistanceDamageType}` : ''}</li>)}</ul></div>
        )}
        <p><strong>Advancement:</strong> {advancementPreview.pendingCrawlerLevels > 0 ? `${advancementPreview.pendingCrawlerLevels} crawler level-up event${advancementPreview.pendingCrawlerLevels === 1 ? '' : 's'} waiting · ` : ''}{pet.unspentStatPoints} unspent Stat Point{pet.unspentStatPoints === 1 ? '' : 's'}.</p>
        <p><strong>Mana:</strong> {pet.currentMana}/{maxMana}{pet.manaBonus > 0 ? ` · +${pet.manaBonus} capacity from pet-specific benefits` : ''}</p>
        {pet.matureBenefit && <p><strong>Level-15 Benefit:</strong> {petMatureBenefitLabel(pet.matureBenefit)}{pet.matureBenefit.notes ? ` · ${pet.matureBenefit.notes}` : ''}</p>}
        {petSpells.length > 0 && <div><strong>Pet Spells</strong><ul>{petSpells.map((spell) => <li key={spell.id}>{spell.name} · Rank {petAssignedSpellRank(pet, spell, character.floor)}{pet.spellRanks[spell.id] ? ' (GM)' : ' (Floor)'} · {spell.manaCost ?? 0} Mana</li>)}</ul></div>}
        {pet.spellIds.some((id) => !contentRegistry.getSkill(id)) && <p className="dcc-creator-warn">One or more GM-assigned pet Spell IDs are not available in this content catalogue.</p>}
        {pet.gearItemIds.length > 0 && <div><strong>Assigned Pet Gear</strong><ul>{pet.gearItemIds.map((id, index) => <li key={`${id}-${index}`}>{petGear[index]?.name ?? id}</li>)}</ul></div>}
        <p className="dcc-creator-help">Pet Attacks and rollable Skills/abilities use Rank equal to the current Floor. Pet Spells default to Floor but may have a GM Rank override. Players commit earned pet level-ups through Level Up Pet; afterward only the GM can correct Stats or the maturity choice. Pet gear remains owned by the crawler rather than becoming a second inventory.</p>
        {(equipment.modifiers.move !== 0 || equipment.modifiers.step !== 0 || equipment.modifiers.moveMultiplier !== 1 || equipment.modifiers.stepMultiplier !== 1) && <p>Equipment movement: Move ×{equipment.modifiers.moveMultiplier} {sign(equipment.modifiers.move)}ft; Step ×{equipment.modifiers.stepMultiplier} {sign(equipment.modifiers.step)}ft.</p>}
        {pet.notes && <p><strong>Notes:</strong> {pet.notes}</p>}
      </div>
    </section>
  );
}


function CompanionHealthBar({ pet, equipment }: { pet: PetCompanion; equipment?: PetEquipment }) {
  const bar = petHealthBar(pet, equipment);
  const slots = Array.from({ length: bar.maxSlots }, (_, index) => ({
    index,
    percent: Math.round(((index + 1) / bar.maxSlots) * 100),
    marked: index >= bar.slotsRemaining,
  }));
  const rows = [slots.slice(0, 5), slots.slice(5)].filter((row) => row.length > 0);

  return (
    <div className="dcc-healthbar dcc-companion-sheet-healthbar" aria-label={`${pet.name} Health Bar: ${bar.slotsRemaining} of ${bar.maxSlots} slots, ${bar.slotValue} Health per slot`}>
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} className="dcc-healthbar-row">
          {row.map((slot) => (
            <div key={slot.index} className="dcc-healthbar-cell">
              <div className={slot.marked ? 'dcc-healthbox is-marked' : 'dcc-healthbox'} title={`${bar.slotValue} damage marks this box`}>
                <span className="dcc-healthbox-value">{bar.slotValue}</span>
                <span className="dcc-healthbox-dot" aria-hidden="true" />
              </div>
              <span className="dcc-healthbox-percent">{slot.percent}%</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}


const STANDARD_MATURE_CHOICES = [
  { id: 'earthquake' as const, label: 'Earthquake', detail: 'Move before a melee Attack, then roll 1d12; 10+ triggers Take Down in the Burst. Cooldown 5 hours.' },
  { id: 'wrecking-ball' as const, label: 'Wrecking Ball', detail: 'Once per day, spend Move + Attack to deal Size d6 + Floor Bludgeoning in a Line.' },
  { id: 'spells' as const, label: 'Spells', detail: 'The pet may cast assigned Spells, spending its own Mana.' },
  { id: 'regeneration' as const, label: 'Regeneration', detail: 'Death returns the pet with +25% Stats and a new weakness; every fifth death reverts it to Level 1. GM adjudication required.' },
];

function CompanionLevelUpPanel({ pet, crawlerLevel, busy, onCancel, onConfirm }: {
  pet: PetCompanion;
  crawlerLevel: number;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (allocation: PetLevelUpAllocation, matureChoice: PetMatureBenefitChoice | null) => Promise<boolean>;
}) {
  const preview = previewPlayerPetAdvancement(pet, crawlerLevel);
  const [allocation, setAllocation] = useState<PetLevelUpAllocation>({ str: 0, con: 0, dex: 0, cha: 0 });
  const [benefitKey, setBenefitKey] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [message, setMessage] = useState('');
  const allocated = allocation.str + allocation.con + allocation.dex + allocation.cha;
  const remaining = preview.pointsAvailable - allocated;

  function setPoints(stat: keyof PetLevelUpAllocation, value: number) {
    const next = Math.max(0, Math.floor(value) || 0);
    setReviewing(false);
    setAllocation((current) => ({ ...current, [stat]: next }));
  }

  function matureChoice(): PetMatureBenefitChoice | null {
    if (!preview.requiresMatureBenefit) return null;
    if (benefitKey.startsWith('custom:')) return { id: 'custom', customOptionId: benefitKey.slice('custom:'.length) };
    if (benefitKey === 'earthquake' || benefitKey === 'wrecking-ball' || benefitKey === 'spells' || benefitKey === 'regeneration') return { id: benefitKey };
    return null;
  }

  const selectedChoice = matureChoice();
  const selectedCustom = selectedChoice?.id === 'custom' ? pet.customMatureBenefits.find((entry) => entry.id === selectedChoice.customOptionId) ?? null : null;
  const benefitLabel = selectedChoice?.id === 'custom'
    ? selectedCustom?.name ?? 'Custom benefit'
    : selectedChoice ? PET_MATURE_BENEFIT_LABELS[selectedChoice.id] : '';
  const canReview = preview.canAdvance && remaining === 0 && (!preview.requiresMatureBenefit || selectedChoice !== null);

  if (!preview.canAdvance) {
    return (
      <section className="dcc-creator-card dcc-companion-levelup">
        <h2>Level Up {pet.name}</h2>
        <p className="dcc-empty">No pet advancement is currently waiting.</p>
        <button className="dcc-creator-secondary" onClick={onCancel}>Close</button>
      </section>
    );
  }

  return (
    <section className="dcc-creator-card dcc-companion-levelup">
      <h2>{preview.levelsGained > 0 ? `Level Up ${pet.name}` : `Finish ${pet.name}'s Advancement`}</h2>
      <p className="dcc-creator-help">
        {preview.pendingCrawlerLevels > 0
          ? `Crawler Level ${crawlerLevel} contains ${preview.pendingCrawlerLevels} unprocessed crawler level-up event${preview.pendingCrawlerLevels === 1 ? '' : 's'} for this pet. This confirmation applies all of them at once.`
          : 'This pet has previously earned advancement that still needs to be finalized.'}
      </p>
      <div className="dcc-companion-vitals">
        <div><span>Pet Level</span><strong>{preview.fromLevel}{preview.toLevel !== preview.fromLevel ? ` → ${preview.toLevel}` : ''}</strong></div>
        <div><span>Pet levels gained</span><strong>{preview.levelsGained}</strong></div>
        <div><span>Stat Points</span><strong>{preview.pointsAvailable}</strong></div>
        <div><span>Remaining</span><strong>{remaining}</strong></div>
      </div>

      {!reviewing ? (
        <>
          <div className="dcc-edit-sheet-section">
            <h3>Allocate Stat Points</h3>
            <p className="dcc-creator-help">Allocate every available point before continuing. Intelligence cannot receive pet level-up points. Nothing below is saved until final confirmation.</p>
            <div className="dcc-companion-stat-grid">
              {(['str', 'con', 'dex', 'cha'] as (keyof PetLevelUpAllocation)[]).map((stat) => (
                <label key={stat} className="dcc-creator-field">
                  {STAT_LABELS[stat].abbr} {pet.unenhancedStats[stat]} → {pet.unenhancedStats[stat] + allocation[stat]}
                  <input
                    type="number"
                    min={0}
                    max={preview.pointsAvailable}
                    step={1}
                    value={allocation[stat]}
                    disabled={busy}
                    onChange={(event) => setPoints(stat, Number(event.target.value))}
                  />
                </label>
              ))}
              <label className="dcc-creator-field">
                INT {pet.unenhancedStats.int}
                <input type="number" value={0} disabled />
                <span className="dcc-creator-help">Locked by the pet level-up rule.</span>
              </label>
            </div>
            {remaining < 0 && <p className="dcc-creator-warn">You assigned {-remaining} more Stat Point{remaining === -1 ? '' : 's'} than are available.</p>}
          </div>

          {preview.requiresMatureBenefit && (
            <div className="dcc-edit-sheet-section">
              <h3>Choose Level-15 Benefit</h3>
              <p className="dcc-creator-help">This choice is committed with the level-up. Afterward, only the GM can change it.</p>
              <label className="dcc-creator-field">Maturity benefit
                <select value={benefitKey} disabled={busy} onChange={(event) => { setBenefitKey(event.target.value); setReviewing(false); }}>
                  <option value="">Choose benefit…</option>
                  {STANDARD_MATURE_CHOICES.map((entry) => <option key={entry.id} value={entry.id}>{entry.label}</option>)}
                  {pet.customMatureBenefits.map((entry) => <option key={entry.id} value={`custom:${entry.id}`}>{entry.name} (GM custom)</option>)}
                </select>
              </label>
              {benefitKey && !benefitKey.startsWith('custom:') && <p className="dcc-creator-help">{STANDARD_MATURE_CHOICES.find((entry) => entry.id === benefitKey)?.detail}</p>}
              {selectedCustom && <p className="dcc-creator-help"><strong>{selectedCustom.name}:</strong> {selectedCustom.notes || 'GM-defined custom maturity benefit.'}</p>}
              {pet.customMatureBenefits.length === 0 && <p className="dcc-creator-help">No GM-defined custom Level-15 benefits are available for this pet.</p>}
            </div>
          )}

          <div className="dcc-creator-nav">
            <button disabled={busy || !canReview} onClick={() => { setMessage(''); setReviewing(true); }}>Review Allocation</button>
            <button className="dcc-creator-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
          </div>
        </>
      ) : (
        <div className="dcc-edit-sheet-section">
          <h3>Final Confirmation</h3>
          <p><strong>Level:</strong> {preview.fromLevel} → {preview.toLevel}</p>
          <ul>
            {(['str', 'con', 'dex', 'cha'] as (keyof PetLevelUpAllocation)[]).filter((stat) => allocation[stat] > 0).map((stat) => (
              <li key={stat}>{STAT_LABELS[stat].abbr}: {pet.unenhancedStats[stat]} → {pet.unenhancedStats[stat] + allocation[stat]} (+{allocation[stat]})</li>
            ))}
            {preview.pointsAvailable === 0 && <li>No Stat Points are being allocated.</li>}
          </ul>
          {preview.requiresMatureBenefit && <p><strong>Level-15 Benefit:</strong> {benefitLabel}</p>}
          <p className="dcc-creator-warn"><strong>Confirm carefully.</strong> After this save, the player sheet cannot undo or edit these Stat allocations or the Level-15 benefit. Only the GM Console can correct them.</p>
          {message && <p className="dcc-save-status dcc-save-warn" role="alert">{message}</p>}
          <div className="dcc-creator-nav">
            <button disabled={busy} onClick={() => void onConfirm(allocation, selectedChoice).then((ok) => { if (!ok) setMessage('The level-up was not committed. The crawler or pet may have changed; reopen the level-up and review it again.'); })}>Confirm Level Up</button>
            <button className="dcc-creator-secondary" disabled={busy} onClick={() => setReviewing(false)}>Back</button>
            <button className="dcc-creator-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}

function CompanionEditor({ existing, ownerLevel, busy, onCancel, onSave }: {
  existing: PetCompanion | null;
  ownerLevel: number;
  busy: boolean;
  onCancel: () => void;
  onSave: (pet: PetCompanion) => Promise<boolean>;
}) {
  const [pet, setPet] = useState<PetCompanion>(() => existing ?? createPetCompanion({ id: petId(), name: 'New Companion', species: 'Animal', origin: 'earth-born', ownerLevel, now: new Date().toISOString() }));
  const [message, setMessage] = useState('');
  const maxHealth = petMaxHealthSlots(pet);
  const problems = validatePetCompanion(pet);

  function patch(next: Partial<PetCompanion>) {
    setPet((current) => {
      const updated = { ...current, ...next };
      const max = petMaxHealthSlots(updated);
      return { ...updated, healthBarSlotsRemaining: Math.min(updated.healthBarSlotsRemaining, max) };
    });
  }

  function setOrigin(origin: PetOrigin) {
    setPet((current) => {
      const earth = origin === 'earth-born';
      const next: PetCompanion = {
        ...current,
        origin,
        crawlerNumber: earth ? current.crawlerNumber : null,
        unenhancedStats: { ...current.unenhancedStats, int: earth ? 1 : current.unenhancedStats.int },
      };
      return next;
    });
  }

  function setAttitude(attitude: PetAttitude) {
    if (attitude !== 'bonded') {
      patch({ attitude, role: null, specialAbilities: [], resistanceDamageType: '' });
      return;
    }
    patch({ attitude, role: pet.role ?? 'utility', specialAbilities: pet.role ? pet.specialAbilities : ['heightened-senses', 'move-60'] });
  }

  function setRole(role: PetRole) {
    if (role === 'aggressive') patch({ role, specialAbilities: [], resistanceDamageType: '' });
    else patch({ role, specialAbilities: [], resistanceDamageType: '' });
  }

  function toggleAbility(ability: PetSpecialAbilityId) {
    const max = pet.role === 'tank' ? 1 : pet.role === 'utility' ? 2 : 0;
    if (max === 0) return;
    const selected = pet.specialAbilities.includes(ability);
    if (selected) patch({ specialAbilities: pet.specialAbilities.filter((id) => id !== ability), resistanceDamageType: ability === 'resistance-uncommon' ? '' : pet.resistanceDamageType });
    else if (pet.specialAbilities.length < max) patch({ specialAbilities: [...pet.specialAbilities, ability] });
  }

  return (
    <section className="dcc-creator-card dcc-companion-editor">
      <h2>{existing ? `Edit ${existing.name}` : 'Add Animal Companion'}</h2>
      <p className="dcc-creator-help">This stores the pet with the crawler. It does not create another crawler account or another inventory.</p>
      <div className="dcc-companion-form-grid">
        <label className="dcc-creator-field">Name<input value={pet.name} onChange={(e) => patch({ name: e.target.value })} /></label>
        <label className="dcc-creator-field">Species / type<input value={pet.species} onChange={(e) => patch({ species: e.target.value })} /></label>
        <label className="dcc-creator-field">Origin<select value={pet.origin} onChange={(e) => setOrigin(e.target.value as PetOrigin)}><option value="earth-born">Earth-born</option><option value="dungeon-born">Dungeon-born</option></select></label>
        {pet.origin === 'earth-born' && <label className="dcc-creator-field">Crawler ID (optional)<input value={pet.crawlerNumber ?? ''} onChange={(e) => patch({ crawlerNumber: e.target.value || null })} /></label>}
        <label className="dcc-creator-field">Level<input type="number" min={1} value={pet.level} disabled /><span className="dcc-creator-help">Use Level Up Pet for player advancement; the GM may override progression.</span></label>
        <label className="dcc-creator-field">Size<select value={pet.size} onChange={(e) => patch({ size: Number(e.target.value) })}>{PET_SIZE_VALUES.map((size) => <option key={size} value={size}>{sizeName(size)} ({size})</option>)}</select></label>
        <label className="dcc-creator-field">Attitude<select value={pet.attitude} onChange={(e) => setAttitude(e.target.value as PetAttitude)}>{ATTITUDES.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}</select></label>
        {pet.attitude === 'bonded' && <label className="dcc-creator-field">Role<select value={pet.role ?? ''} onChange={(e) => setRole(e.target.value as PetRole)}><option value="">Choose role…</option>{ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}</select></label>}
        <label className="dcc-creator-field">Health slots remaining<input type="number" min={0} max={maxHealth} value={pet.healthBarSlotsRemaining} onChange={(e) => patch({ healthBarSlotsRemaining: Math.max(0, Math.min(maxHealth, Number(e.target.value) || 0)) })} /><span className="dcc-creator-help">Maximum {maxHealth} from Size + Level, capped at 10.</span></label>
      </div>

      <div className="dcc-edit-sheet-section">
        <h3>Stats</h3>
        <p className="dcc-creator-help">Pet Stats are locked here. Use Level Up Pet to allocate earned Stat Points; after confirmation only the GM can correct them. Intelligence cannot receive ordinary pet level-up points.</p>
        <div className="dcc-companion-stat-grid">
          {STAT_IDS.map((stat: StatId) => <label key={stat} className="dcc-creator-field">{STAT_LABELS[stat].abbr}<input type="number" min={1} disabled value={pet.unenhancedStats[stat]} onChange={(e) => patch({ unenhancedStats: { ...pet.unenhancedStats, [stat]: Math.max(1, Number(e.target.value) || 1) } })} /></label>)}
        </div>
      </div>

      {pet.attitude === 'bonded' && pet.role && pet.role !== 'aggressive' && (
        <div className="dcc-edit-sheet-section">
          <h3>Special Abilities</h3>
          <p className="dcc-creator-help">{pet.role === 'tank' ? 'Tank chooses exactly 1.' : 'Utility chooses exactly 2.'}</p>
          <div className="dcc-companion-ability-list">
            {ABILITIES.map(([id, label]) => <label key={id}><input type="checkbox" checked={pet.specialAbilities.includes(id)} onChange={() => toggleAbility(id)} /> {label}</label>)}
          </div>
          {pet.specialAbilities.includes('resistance-uncommon') && <label className="dcc-creator-field">Resistance damage type<input value={pet.resistanceDamageType} placeholder="e.g. Acid" onChange={(e) => patch({ resistanceDamageType: e.target.value })} /></label>}
        </div>
      )}

      <label className="dcc-creator-field">Notes<textarea value={pet.notes} rows={3} onChange={(e) => patch({ notes: e.target.value })} /></label>
      {problems.length > 0 && <ul className="dcc-creator-problems">{problems.map((problem) => <li key={problem}>{problem}</li>)}</ul>}
      {message && <p className="dcc-save-status dcc-save-warn">{message}</p>}
      <div className="dcc-creator-nav">
        <button disabled={busy || problems.length > 0} onClick={() => void onSave(pet).then((ok) => { if (!ok) setMessage('The companion could not be saved. Review the fields above.'); })}>Save Companion</button>
        <button className="dcc-creator-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </section>
  );
}
